import app from 'firebase/app'
import 'firebase/auth'
import 'firebase/database'
import 'firebase/functions'
import 'firebase/analytics'
import 'firebase/storage';
import 'firebase/firebase-firestore'
import moment from 'moment'
import 'moment/locale/fi'
import FuseCache from './fusecache'
import { v4 as uuidv4 } from 'uuid'
import { getReservation as getReservationShell } from '../variables'
import { managerIdsOf } from '../utils/teams'
import { runPool } from '../utils/pool'
import { MULTIRESOURCE_TYPE, DAYRESOURCE_TYPE, filterSnap, isPhysicalResourceDoc, keepMultiResourceDoc, keepDayResourceDoc } from '../utils/resources'
import {
  buildRow as buildExtraItemRow,
  candidatesFromEntries as extraItemCandidates,
  dateRangeKeys as extraItemDateRange,
  dedupeCandidates as dedupeExtraItemCandidates,
  exceedsReadLimit as exceedsExtraItemReadLimit,
  sortRows as sortExtraItemRows,
} from '../utils/extraitems'
import { defaultKeys } from 'rrule/dist/esm/rrule'


const config = {
  apiKey: 'AIzaSyB_aqXQI6DuVinrjeKX7doEHWEd-dIN1mQ',
  authDomain: 'falcon-328a1.firebaseapp.com',
  databaseURL: 'https://falcon-328a1.firebaseio.com',
  projectId: 'falcon-328a1',
  storageBucket: 'falcon-328a1.appspot.com',
  messagingSenderId: '640167253821',
  appId: '1:640167253821:web:48fd942ebc8e0923a59754',
  measurementId: 'G-F1DTX4NT39',
}

// Reservations store times in this format; the conflict/pricing callables want the
// space-separated one. Activities use 'YYYY-MM-DDTHH:mm' (UTC wall clock).
const RESERVATION_TIMEFORMAT = 'YYYY-MM-DDTHH:mm:ssZ'
const SLOT_TIMEFORMAT = 'YYYY-MM-DD HH:mm'

// The extras report reads one collection per day and then one document per hit. Six and
// eight keep a single connection busy without the SDK queueing behind itself, and without a
// wide range opening hundreds of sockets at once.
const EXTRAS_DAY_CONCURRENCY = 6
const EXTRAS_DOC_CONCURRENCY = 8

// resourcepricings.packagebandmode: the default behaviour is the ABSENCE of the key, not a
// value meaning "default". Every pricing write here is set({merge: true}), which never removes
// a key, so anything other than a real mode has to be turned into a field delete on an
// existing document, or dropped from a new one - add() rejects a delete sentinel.
//
// The dialog's first option is the sentinel 'default' rather than an empty selection, because
// rjsf pre-selects the first oneOf branch's const and pre-selecting a real mode would change
// what a venue charges without anybody choosing it (see utils/pricing.js). That sentinel is
// never stored: it lands here and becomes an absent field, which is exactly what the backend
// reads as today's behaviour.
//
// Deliberately a module-level function rather than a class field: it needs no `this`, and one
// less class field is one less thing that vanishes when Fast Refresh reloads this module
// without re-running the constructor.
const PACKAGE_BAND_STORED_MODES = ['linear', 'ignore']

const normalisePackageBandMode = (config, existingDocument) => {
  if (!config) return config
  if (PACKAGE_BAND_STORED_MODES.includes(config.packagebandmode)) return config
  if (!('packagebandmode' in config) && !existingDocument) return config
  if (existingDocument) {
    config.packagebandmode = app.firestore.FieldValue.delete()
  } else {
    delete config.packagebandmode
  }
  return config
}

class Firebase {
  constructor() {
    app.initializeApp(config)
    this.auth = app.auth()
    this.db = app.database()
    this.firestore = app.firestore()
    this.analytics = app.analytics()
    this.functions = app.functions()
    this.customerid = null
    this.storage = app.storage()
    this.cache = new FuseCache()
    this.TaskEvent = app.storage.TaskEvent
  }

  getRandomId = () => this.firestore.collection('random').doc().id
  getTimestamp = (dateString) => app.firestore.Timestamp.fromDate(moment(dateString, 'DD.MM.YYYY').toDate())
  getTimestampFromMoment = (dateMoment) => app.firestore.Timestamp.fromDate(dateMoment.toDate())
  getToday = () => app.firestore.Timestamp.fromDate(new Date())
  getCustomerId = () => this.customerid

  getAdminAPICall = () => {
    this.functions.region = 'europe-west1'
    return this.functions.httpsCallable('adminapi-call')
  }



  // *** Auth API ***
  doCreateUserWithEmailAndPassword = (email, password) => this.auth.createUserWithEmailAndPassword(email, password)

  doSignInWithEmailAndPassword = (email, password) => this.auth.signInWithEmailAndPassword(email, password)

  doSignOut = () => this.auth.signOut()

  doPasswordReset = (email) => this.auth.sendPasswordResetEmail(email)

  doPasswordUpdate = (password) => this.auth.currentUser.updatePassword(password)

  listenUiVersion = () => {
    // console.log('return ref')
    return this.db.ref('/manageui').orderByKey()
  }

  listenUiVersionVenue = (env) => {
    // console.log('return ref')
    return this.db.ref('/venuemanager/' + env).orderByKey()
  }

  listenLastread = () => {
    // console.log('listening lastread', '/venuemanagernews/' + this.auth.currentUser.uid)
    return this.db.ref('/venuemanagernews/' + this.auth.currentUser.uid).orderByKey()
  }

  updateLastread = () => {
    this.db.ref('/venuemanagernews/' + this.auth.currentUser.uid).set({ lastread: Date.now() })
  }

  listenFeatures = () => {
    if (this.customerid) {
      return this.firestore.collection('venuemanagerfeatures').doc(this.customerid)
    } else {
      return null
    }
  }

  listenModules = () => {
    if (this.customerid) {
      return this.firestore.collection('subscriptions').doc(this.customerid)
    } else {
      return null
    }
  }

  enableModule = async (module, price) => {
    try {
      this.functions.region = 'europe-west1'
      const enableModule = this.functions.httpsCallable('customer-modules')
      const response = await enableModule({ action: 'enable', module, price, cid: this.customerid })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  disableModule = async (module) => {
    try {
      this.functions.region = 'europe-west1'
      const disableModule = this.functions.httpsCallable('customer-modules')
      const response = await disableModule({ action: 'disable', module, cid: this.customerid })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  listenUnpaidInvoiceAlert = () => {
    console.log('listening unpaidInvoiceAlert', '/customerinvoices/' + this.customerid)
    if (this.customerid) {
      return this.firestore.collection('customerinvoices').doc(this.customerid)
    } else {
      return null
    }
  }


  // *** User API ***
  user = (uid) => this.firestore.collection('users').doc(uid)
  users = () => this.firestore.collection('users')

  logEvent = (eventName) => {
    // console.log('fb event')
    this.analytics.logEvent('goal_completion', { name: 'lever_puzzle' })
  }

  getCustomerRef = () => {
    return this.firestore.collection('customers').doc(this.customerid)
  }

  getCustomerReportRef = () => {
    return this.firestore.collection('stats').doc(this.customerid)
  }

  getCustomerProshopRef = () => {
    return this.firestore.collection('proshop').doc(this.customerid)
  }

  getCustomerBookingIndexRef = () => {
    return this.firestore.collection('indexes').doc('bookings').collection(this.customerid)
  }

  getCustomerBookingIndexingRef = () => {
    return this.firestore.collection('bookings').doc(this.customerid)
  }

  getCustomerMessageRef = () => {
    return this.firestore.collection('messages').doc(this.customerid).collection('active')
  }

  getRTDBCustomerRef = () => {
    return this.db.ref('customers/' + this.customerid)
  }

  // users-updateRole sets the role on users/{uid} and this venue's persons/{uid}; the claims
  // follow on the user's next token refresh. Throws on failure, including the callable's own
  // { error, message } answer, so the caller can show it.
  updateUserRole = async (userid, role) => {
    this.functions.region = 'europe-west1'
    const updateRole = this.functions.httpsCallable('users-updateRole')
    const response = await updateRole({ userid, cid: this.customerid, role })
    const data = (response && response.data) || {}
    if (data.error) throw new Error(data.message || 'Roolin päivitys epäonnistui')
    return data
  }

  // The login behind a person: users/{uid} with its venue role (docs/roles.md). A person
  // without a users doc has no login and can't be given a role.
  getUserAccount = async (uid) => {
    const doc = await this.user(uid).get()
    if (!doc.exists) return { exists: false }
    const data = doc.data() || {}
    return { exists: true, role: data.role || 'USER', customerIds: data.customerIds || [] }
  }


  /* Resource */

  // getResourceCategories = async () => {
  //   // sport
  //   return [
  //     { type: 'category', value: 'tennis', label: 'Tennis' },
  //     { type: 'category', value: 'sulkapallo', label: 'Sulkapallo' },
  //   ]
  // }

  createResource = async (resource) => {
    resource.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('resources').add(resource)
  }

  updateResource = async (resource) => {
    resource.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('resources').doc(resource.id).set(resource, { merge: true })
  }

  deleteResource = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resources').doc(id).set(r, { merge: true })
  }

  getResourse = async (id) => {
    return await this.getCustomerRef().collection('resources').doc(id).get()
  }

  // Real (bookable) resources only: multiresources share the collection but are filtered out
  // here so that no calendar, POS or picker shows them. Use getMultiResources() for those.
  getResourses = async () => {
    let r = await this.getCustomerRef().collection('resources').get()
    return filterSnap(r, isPhysicalResourceDoc)
  }

  // Not a raw query: callers only use .onSnapshot / .get, both of which hand back a
  // snapshot without multiresources. onSnapshot still returns the unsubscribe function.
  listenResourses = () => {
    return this._filteredResourceQuery(isPhysicalResourceDoc)
  }

  _filteredResourceQuery = (keep) => {
    const query = this.getCustomerRef().collection('resources').orderBy('displayName')
    return {
      onSnapshot: (onNext, onError) => query.onSnapshot((snap) => onNext(filterSnap(snap, keep)), onError),
      get: async () => filterSnap(await query.get(), keep),
    }
  }

  getMultiResources = async () => {
    let r = await this.getCustomerRef().collection('resources').get()
    return filterSnap(r, keepMultiResourceDoc)
  }

  listenMultiResources = () => {
    return this._filteredResourceQuery(keepMultiResourceDoc)
  }

  // Day resources (päiväresurssit): booked per calendar day through dayreservations-call, kept out of
  // every hourly list by getResourses()/listenResourses(). See docs/features/day-resources.md.
  getDayResources = async () => {
    let r = await this.getCustomerRef().collection('resources').get()
    return filterSnap(r, keepDayResourceDoc)
  }

  listenDayResources = () => {
    return this._filteredResourceQuery(keepDayResourceDoc)
  }

  createDayResource = async (dayresource) => {
    return await this.createResource({ ...dayresource, type: DAYRESOURCE_TYPE })
  }

  updateDayResource = async (dayresource) => {
    return await this.updateResource({ ...dayresource, type: DAYRESOURCE_TYPE })
  }

  // Bookings whose period overlaps from..to (YYYY-MM-DD, inclusive). Firestore allows the range
  // filter on one field only, so endDate >= from is queried and startDate <= to is checked here.
  listenDayReservations = (from, to, onNext, onError) => {
    return this.getCustomerRef()
      .collection('dayreservations')
      .where('endDate', '>=', from)
      .onSnapshot((snap) => {
        const bookings = snap.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((b) => b.startDate <= to)
        onNext(bookings)
      }, onError)
  }

  dayReservationsCall = async (command, payload = {}) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('dayreservations-call')
      const response = await call({ customerid: this.customerid, command, ...payload })
      return response.data
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  quoteDayReservation = async (payload) => this.dayReservationsCall('quote', payload)

  createDayReservation = async (payload) => this.dayReservationsCall('create', payload)

  updateDayReservation = async (payload) => this.dayReservationsCall('update', payload)

  cancelDayReservation = async (id) => this.dayReservationsCall('cancel', { id })

  addDayReservationPayment = async (id, payment) => this.dayReservationsCall('addpayment', { id, ...payment })

  createMultiResource = async (multiresource) => {
    return await this.createResource({ ...multiresource, type: MULTIRESOURCE_TYPE })
  }

  updateMultiResource = async (multiresource) => {
    return await this.updateResource({ ...multiresource, type: MULTIRESOURCE_TYPE })
  }

  // createResourceCalendar = (resourcecalendar) => {}

  // updateResourceCalendar = (resourcecalendar) => {}

  // deleteResourceCalendar = (id) => {}

  // createResourcePricing = (resourcepricing) => {}

  // updateResourcePricing = (resourcepricing) => {}

  // deleteResourcePricing = (id) => {}

  /* Provider */
  createProvider = async (provider) => {
    provider.creatorId = this.auth.currentUser.uid
    if (!provider.code || provider.code === '') provider.code = provider.name.toLowerCase().replace(/\s/g, '')
    return await this.getCustomerRef().collection('provider').add(provider)
  }

  updateProvider = async (provider) => {
    provider.updaterId = this.auth.currentUser.uid
    if (!provider.code || provider.code === '') provider.code = provider.name.toLowerCase().replace(/\s/g, '')
    return await this.getCustomerRef().collection('provider').doc(provider.id).set(provider, { merge: true })
  }

  deleteProvider = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('provider').doc(id).set(r, { merge: true })
  }

  getProvider = async (id) => {
    return await this.getCustomerRef().collection('provider').doc(id).get()
  }

  getProviders = async () => {
    let r = await this.getCustomerRef().collection('provider').get()
    return r
  }


  /* Activity */

  // Assign ids to extrainfo questions (id auto-generated at save, so the editor UI
  // does not have to invent them). Mirrors resolveEventTicketExtraInfo, minus the
  // variation remapping: activities have no variations to point at a question.
  resolveActivityExtraInfo = (activity) => {
    if (!activity.extrainfo || activity.extrainfo.length === 0) return activity
    activity.extrainfo = activity.extrainfo.map((e) => ({ ...e, id: e.id || this.getRandomId() }))
    return activity
  }

  createActivity = async (activity) => {
    activity = this.resolveActivityExtraInfo(activity)
    activity.creatorId = this.auth.currentUser.uid
    activity.timestamp = this.getTimestamp(moment.utc(activity.start).format('DD.MM.YYYY'))
    activity.date = moment.utc(activity.start).format('YYYYMMDD')
    activity.created = this.getToday()
    return await this.getCustomerRef().collection('activities').add(activity)
  }

  // Activity templates (docs/features/activity-templates.md): everything about an activity
  // except its dates, which are stored as minutes before the start. Client-only data - the
  // backend only ever sees the activities created from them.
  listenActivityTemplates = () => {
    return this.getCustomerRef().collection('activitytemplates')
  }

  // Per-occurrence flags the creator form carries; an activity built from the template sets them.
  stripActivityTemplate = (template) => {
    const payload = { ...template }
    // question ids are assigned per activity, not per template
    if (Array.isArray(payload.extrainfo)) payload.extrainfo = payload.extrainfo.map(({ id, ...q }) => q)
    ;['id', 'ispublished', 'entryendProcessed', 'pinned', 'start', 'end'].forEach((field) => delete payload[field])
    return payload
  }

  createActivityTemplate = async (template) => {
    const payload = this.stripActivityTemplate(template)
    payload.state = 'active'
    payload.creatorId = this.auth.currentUser.uid
    payload.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('activitytemplates').add(payload)
  }

  updateActivityTemplate = async (template) => {
    const payload = this.stripActivityTemplate(template)
    payload.updaterId = this.auth.currentUser.uid
    payload.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('activitytemplates').doc(template.id).set(payload, { merge: true })
  }

  deleteActivityTemplate = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('activitytemplates').doc(id).set(data, { merge: true })
  }

  // Recurring activities (docs/features/recurring-activities.md): the series doc lives in
  // recurrents/{id} (collection: 'activities'); the backend generates its occurrences into
  // activities with recurrentid. A draft series has no occurrences.
  listenRecurrentActivities = () => {
    return this.getCustomerRef().collection('recurrents').where('collection', '==', 'activities')
  }

  // No `updated` here: on recurrents it is the backend trigger's own marker
  // (recurrents-updatecustomerrecurrents skips a write whose `updated` is a fresh Timestamp, to
  // stop its own write-back from looping). A client Timestamp there could make it skip a
  // draft -> published update, which is what generates the occurrences.
  updateRecurrentActivity = async (id, fields) => {
    const payload = { ...fields }
    delete payload.id
    delete payload.updated
    payload.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('recurrents').doc(id).set(payload, { merge: true })
  }

  getRecurrentOccurrences = async (recurrentid) => {
    const r = await this.getCustomerRef().collection('activities').where('recurrentid', '==', recurrentid).get()
    return r.docs.map((doc) => ({ ...doc.data(), id: doc.id })).filter((a) => a.state !== 'deleted')
  }

  createRecurrentActivity = async (activity) => {
    activity = this.resolveActivityExtraInfo(activity)
    activity.creatorId = this.auth.currentUser.uid
    activity.timestamp = this.getTimestamp(moment.utc().format('DD.MM.YYYY'))
    activity.date = moment.utc().format('YYYYMMDD')
    activity.created = this.getToday()
    activity.collection = 'activities'
    activity.entryunit = 'minutes'
    return await this.getCustomerRef().collection('recurrents').add(activity)
  }


  updateActivity = async (activity) => {
    const payload = this.resolveActivityExtraInfo({ ...activity })
    payload.updaterId = this.auth.currentUser.uid
    delete payload.participants
    delete payload.queue
    delete payload.payments
    delete payload.refunds
    payload.timestamp = this.getTimestamp(moment.utc(payload.start).format('DD.MM.YYYY'))
    payload.date = moment.utc(payload.start).format('YYYYMMDD')
    payload.updated = this.getToday()
    return await this.getCustomerRef().collection('activities').doc(payload.id).set(payload, { merge: true })
  }

  /**
   * Updates a single recurrent activity instance and marks it as excluded from future updates
   */
  updateSingleRecurrentActivity = async (activityid, changes) => {
    this.functions.region = 'europe-west1'
    const updateSingle = this.functions.httpsCallable('activities-updateSingleRecurrentActivity')
    const customerid = this.customerid
    const response = await updateSingle({ customerid, activityid, changes })
    return response
  }

  /**
   * Updates all future instances of a recurrent activity (including current) and the template
   */
  updateFutureRecurrentActivities = async (recurrentId, activityid, changes, fromDate) => {
    this.functions.region = 'europe-west1'
    const updateFuture = this.functions.httpsCallable('activities-updateFutureRecurrentActivities')
    const customerid = this.customerid
    const response = await updateFuture({ customerid, recurrentId, activityid, changes, fromDate })
    return response
  }

  deleteActivity = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('activities').doc(id).set(r, { merge: true })
  }

  getActivity = async (id) => {
    return await this.getCustomerRef().collection('activities').doc(id).get()
  }

  getActivities = async () => {
    let r = await this.getCustomerRef().collection('activities').where('state', '!=', 'deleted').get()
    return r
  }

  /* Activity reservations */

  // Three time formats are in play. Activities store 'YYYY-MM-DDTHH:mm' UTC wall clock,
  // reservations store RESERVATION_TIMEFORMAT, and the conflict/pricing callables want
  // SLOT_TIMEFORMAT. The callables also disagree on the resource key: redis-isconflict
  // takes `resourceid`, testreservations-getpricev4 takes `resourceId`.
  buildActivityReservation = (activity, resource, payers, opts = {}) => {
    const shell = getReservationShell()
    shell.state = 'reserved'
    shell.title = opts.title || (activity.name && activity.name.fi) || ''
    shell.resourcegroups[0].resources = [resource]
    shell.resourcegroups[0].cost = Number(opts.cost) || 0
    shell.resourcegroups[0].start = moment.utc(activity.start).format(RESERVATION_TIMEFORMAT)
    shell.resourcegroups[0].end = moment.utc(activity.end).format(RESERVATION_TIMEFORMAT)
    shell.persons.payers = Array.isArray(payers) ? [...payers] : payers ? [payers] : []
    shell.activityid = activity.id
    shell.activityreservation = true
    delete shell.id
    return shell
  }

  // One reservation per resource. Sequential rather than batched, matching
  // saveMultiReservation -- each create also fires triggerUpdateReservationIndex.
  createActivityReservations = async (activity, resources, payers, opts = {}) => {
    const created = []
    const errors = []
    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i]
      const payload = this.buildActivityReservation(activity, resource, payers, opts)
      const result = await this.createReservation(payload)
      // createReservation returns { error } instead of throwing, so a try/catch here
      // would never fire and a failed row would be lost silently.
      if (!result || result.error || !result.id) {
        errors.push({ resource, error: (result && result.error) || 'create-failed' })
      } else {
        created.push({
          id: result.id,
          resourceid: resource.id,
          resourcename: resource.displayName || '',
          start: payload.resourcegroups[0].start,
          end: payload.resourcegroups[0].end,
          cost: payload.resourcegroups[0].cost,
          attached: 'created',
        })
      }
    }
    return { created, errors }
  }

  // Partial merge: updateReservation stamps updaterId/updated and rebuilds the index.
  attachReservationToActivity = async (activityid, reservationid) => {
    return await this.updateReservation({ id: reservationid, activityid, activityreservation: true })
  }

  detachReservationFromActivity = async (reservationid) => {
    return await this.updateReservation({ id: reservationid, activityid: '', activityreservation: false })
  }

  // Single entry point for creating the link, used by both the activity Varaukset tab
  // and the picker in ReservationModal, so the two sides cannot diverge.
  linkActivityAndReservation = async (activityid, summary) => {
    const snapshot = await this.getActivity(activityid)
    if (!snapshot.exists) return { error: 'activity-not-found' }
    const activity = { id: activityid, ...snapshot.data() }
    const existing = activity.reservations || []
    if (!existing.some((r) => r.id === summary.id)) {
      activity.reservations = [...existing, summary]
      await this.updateActivity(activity)
    }
    return await this.attachReservationToActivity(activityid, summary.id)
  }

  unlinkActivityAndReservation = async (activityid, reservationid) => {
    if (activityid) {
      const snapshot = await this.getActivity(activityid)
      if (snapshot.exists) {
        const activity = { id: activityid, ...snapshot.data() }
        activity.reservations = (activity.reservations || []).filter((r) => r.id !== reservationid)
        await this.updateActivity(activity)
      }
    }
    return await this.detachReservationFromActivity(reservationid)
  }

  // Releasing a booking is a state change, not a delete -- reservations have no hard
  // delete, and only updateReservation refreshes the booking index.
  cancelActivityReservation = async (reservationid) => {
    return await this.updateReservation({ id: reservationid, state: 'cancelled' })
  }

  // Move attached reservations after the activity's time changed.
  moveActivityReservation = async (reservationid, start, end) => {
    const existing = await this.getReservation(reservationid)
    if (!existing.exists) return { error: 'not-found' }
    const reservation = { ...existing.data(), id: reservationid }
    reservation.resourcegroups[0].start = moment.utc(start).format(RESERVATION_TIMEFORMAT)
    reservation.resourcegroups[0].end = moment.utc(end).format(RESERVATION_TIMEFORMAT)
    return await this.updateReservation(reservation)
  }

  // Reservations starting on a day, from the same sharded booking index the court calendars
  // read. Each entry carries start/end/resourceId/title plus a minified `data` copy.
  //
  // Keyed on the START day only (reservations-utils.js:391): a booking that runs past
  // midnight is listed under the day it began, and nothing is indexed under the day it ended.
  getReservationsForDay = async (date) => {
    const key = moment.utc(date, 'YYYY-MM-DD').format('YYYYMMDD')
    const snapshot = await this.listenBookingIndex(key).get()
    const entries = []
    snapshot.forEach((doc) => {
      const shard = doc.data()
      Object.keys(shard).forEach((id) => entries.push({ ...shard[id], id }))
    })
    return entries
  }

  // The reservations that carry extra items (lisätuotteet) over a date range.
  //
  // There is no ranged query for this and there cannot be: a reservation's start time lives
  // at resourcegroups[0].start, inside an array, so Firestore can neither filter nor order by
  // it. The only thing keyed by date is the sharded booking index above, so the range is
  // walked a day at a time - and because that index drops `items`, each day's hits then need
  // their own document read. utils/extraitems.js owns why, and every shaping decision; this
  // method owns only the reads, the concurrency and the progress reporting.
  //
  // Resolves to { rows, warnings, error } and never rejects. A failed day or a failed
  // document is counted in `warnings` instead, so a network blip cannot quietly return a
  // short report that looks complete.
  //
  // If N ever grows enough to matter, the lever is where(documentId(), 'in', chunkOf10) in
  // phase two - 10x fewer round trips, at the cost of this SDK's 10-value cap on `in`.
  getReservationsWithExtras = async (startdate, enddate, resourcesById, onProgress) => {
    const warnings = { faileddays: 0, loadfailed: 0, missingitems: 0 }
    const { days, error } = extraItemDateRange(startdate, enddate)
    if (error) return { rows: [], warnings, error, found: 0 }

    const progress = (phase, done, total) => {
      if (onProgress) onProgress({ phase, done, total })
    }

    // Phase one: sweep the day index for entries carrying the extras marker.
    let sweptdays = 0
    progress('days', 0, days.length)
    const dayresults = await runPool(
      days.map((day) => async () => extraItemCandidates(await this.getReservationsForDay(day), day)),
      EXTRAS_DAY_CONCURRENCY,
      () => {
        sweptdays += 1
        progress('days', sweptdays, days.length)
      }
    )

    const candidates = []
    dayresults.forEach((result) => {
      if (result.error) {
        warnings.faileddays += 1
        console.error('extra items report: day index read failed', result.error)
      } else {
        candidates.push(...result.value)
      }
    })

    // Phase two: the items themselves, one document per reservation. These are billed reads,
    // and the count is known before any of them is issued - so a range with more hits than
    // the ceiling stops here having spent nothing, and reports how many it found.
    const unique = dedupeExtraItemCandidates(candidates)
    if (exceedsExtraItemReadLimit(unique.length)) {
      return { rows: [], warnings, error: 'too-many-reservations', found: unique.length }
    }
    let readdocs = 0
    progress('reservations', 0, unique.length)
    const docresults = await runPool(
      unique.map((candidate) => async () => {
        const doc = await this.getReservation(candidate.id)
        return doc && doc.exists ? { id: doc.id, ...doc.data() } : null
      }),
      EXTRAS_DOC_CONCURRENCY,
      () => {
        readdocs += 1
        progress('reservations', readdocs, unique.length)
      }
    )

    const rows = unique.map((candidate, i) => {
      const result = docresults[i]
      if (result && result.error) console.error('extra items report: reservation read failed', candidate.id, result.error)
      const doc = result && !result.error ? result.value : null
      const row = buildExtraItemRow({ candidate, doc, resourcesById })
      if (row.loadfailed) warnings.loadfailed += 1
      else if (row.missingitems) warnings.missingitems += 1
      return row
    })

    return { rows: sortExtraItemRows(rows), warnings, error: null, found: unique.length }
  }

  // Conflict check for a set of resources over one time range. Returns one row per
  // resource so the caller can name the clashing court.
  checkResourceConflicts = async (start, end, resources) => {
    if (!resources || resources.length === 0) return []
    const slots = resources.map((r) => ({
      start: moment.utc(start).format(SLOT_TIMEFORMAT),
      end: moment.utc(end).format(SLOT_TIMEFORMAT),
      resourceid: r.id,
    }))
    const result = await this.isConflict(slots)
    if (!result || result.error) return { error: (result && result.error) || 'conflictcheck-failed' }
    return result.map((row, i) => ({ ...row, resource: resources[i] }))
  }

  addParticipantsToActivity = async (activityid, participants) => {
    try {
      this.functions.region = 'europe-west1'
      const addParticipants = this.functions.httpsCallable('activities-addParticipants')
      const customerid = this.customerid
      const response = await addParticipants({ customerid, activityid, users: participants })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  // refund says whether the participant's payment should be refunded as part of the removal.
  // Always sent explicitly, so the backend never has to guess. See docs/functions-to-update.txt.
  removeParticipantsFromActivity = async (activityid, participants, refund) => {
    try {
      this.functions.region = 'europe-west1'
      const removeParticipants = this.functions.httpsCallable('activities-removeParticipants')
      const customerid = this.customerid
      const response = await removeParticipants({ customerid, activityid, userids: participants, refund: refund === true })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  addQueuedToActivity = async (activityid, queued) => {
    try {
      this.functions.region = 'europe-west1'
      const addQueued = this.functions.httpsCallable('activities-addQueue')
      const customerid = this.customerid
      const response = await addQueued({ customerid, activityid, users: queued })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  // refund as above. A queued person rarely has a payment, but the manager is asked either
  // way and the answer is passed through rather than assumed.
  removeQueuedFromActivity = async (activityid, queued, refund) => {
    try {
      this.functions.region = 'europe-west1'
      const removeQueued = this.functions.httpsCallable('activities-removeQueue')
      const customerid = this.customerid
      const response = await removeQueued({ customerid, activityid, userids: queued, refund: refund === true })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  moveParticipantToQueue = async (activityid, participant) => {
    try {
      this.functions.region = 'europe-west1'
      const moveBetweenQueueAndParticipants = this.functions.httpsCallable('activities-moveBetweenQueueAndParticipants')
      const customerid = this.customerid
      const response = await moveBetweenQueueAndParticipants({ customerid, activityid, direction: 'to_queue', userids: [participant] })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  moveParticipantFromQueue = async (activityid, participant) => {
    try {
      this.functions.region = 'europe-west1'
      const moveBetweenQueueAndParticipants = this.functions.httpsCallable('activities-moveBetweenQueueAndParticipants')
      const customerid = this.customerid
      const response = await moveBetweenQueueAndParticipants({ customerid, activityid, direction: 'to_participant', userids: [participant] })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  checkinoutParticipant = async (activityid, participant, inout) => {
    try {
      this.functions.region = 'europe-west1'
      const checkinoutParticipant = this.functions.httpsCallable('activities-checkInoutParticipant')
      const customerid = this.customerid
      const response = await checkinoutParticipant({ customerid, activityid, userid: participant, inout })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  // Records a payment against one participant of an activity. amount is in CENTS, to match
  // activity payments[].amount - activity.price is euros, so the caller converts.
  // type is 'cashier' today; see ACTIVITY_PAYMENT_TYPES in ActivityPaymentDialog.
  // identity says which participant the payment belongs to and carries exactly one of
  // paymenttoken, entrytime or participantIndex - see participantIdentity() in
  // ActivityPaymentDialog for how it is chosen, and docs/functions-to-update.txt.
  // Note this resolves with { error } instead of throwing - callers must check for it.
  addActivityPayment = async (activityid, userid, amount, type, identity) => {
    try {
      this.functions.region = 'europe-west1'
      const addPayment = this.functions.httpsCallable('activities-addPayment')
      const customerid = this.customerid
      const data = { customerid, activityid, userid, amount, type, ...(identity || {}) }
      const response = await addPayment(data)
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  /* Person */
  registerPerson = async (person) => {
    person.customerIds = [this.customerid]
    person._tags = [this.customerid]
    person.admincreated = true
    person.confirmed = false
    person.register = this.customerid
    Object.keys(person).forEach(key => {
      if (person[key] === undefined) {
        delete person[key];
      }
    });
    return this.db.ref('reg').push(person)
  }

  // createUser = async (person) => {
  //   delete person.customers
  //   return await this.getCustomerRef().collection('persons').add(person)
  // }

  checkIfEmailExits = (email) => { return false }

  // addNewUser = async (person) => {
  //   // check if user account exists
  //   if(this.checkIfEmailExits(person.email))
  //   // if exist add user to customer with _tags and just remove first and lastname

  //   // if not exists register and add unconfirmed to customer 


  // }

  createPerson = async (person) => {
    delete person.customers
    const rand =
      'ag_' + this.getRandomId() + '_' + person.email.toLowerCase().replace(/\./g, '_dot_').replace(/#/g, '_hash_')
    person.admincreated = true
    person.confirmed = false
    person._tags = [this.customerid]
    try {
      return await this.getCustomerRef().collection('persons').doc(rand).set(person)
    } catch (error) {
      return Promise.resolve(error)
    }
  }

  updatePerson = async (person) => {
    delete person.customers
    person.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('persons').doc(person.id).set(person, { merge: true })
  }

  getPersonCData = async (id) => {
    return await this.getCustomerRef().collection('persons').doc(id).get()
  }

  deletePerson = async (id) => {
    let r = { id: id, state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.updatePerson(r)
  }

  listenPersons = () => {
    return this.getCustomerRef().collection('persons')
  }

  listenCustomerUsers = () => {
    return this.getCustomerRef().collection('persons').where('role', 'in', ['ADMIN', 'CASHIER', 'PARTNER', 'COOPERATIVE'])
  }

  listenPersonsRT = () => {
    // console.log('listening usercache/' + this.customerid)
    return this.db.ref('usercache/' + this.customerid)
  }

  getPersonReservations = (id) => {
    return this.firestore
      .collection('users')
      .doc(id)
      .collection('reservations')
      .where('locationid', '==', this.customerid)
  }

  getOrganisationReservations = async (id) => {
    return await this.getCustomerRef().collection('organisations').doc(id).collection('reservations').get()
  }

  /* personal admin stuff */
  getPersonVenueManagerConfigs = () => {
    return this.firestore
      .collection('users')
      .doc(this.auth.currentUser.uid)
      .collection('venuemanager')
      .doc(this.customerid).get()
  }

  listenPersonVenueManagerConfigs = () => {
    return this.firestore
      .collection('users')
      .doc(this.auth.currentUser.uid)
      .collection('venuemanager')
      .doc(this.customerid)
  }

  savePersonVenueManagerConfigs = (config) => {
    // console.log('savePersonVenueManagerConfigs')
    return this.firestore
      .collection('users')
      .doc(this.auth.currentUser.uid)
      .collection('venuemanager')
      .doc(this.customerid).set(config, { merge: true })
  }

  /* Partner */
  createPartner = async (partner) => {
    partner.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('partners').add(partner)
  }

  updatePartner = async (partner) => {
    partner.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('partners').doc(partner.id).set(partner, { merge: true })
  }

  deletePartner = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('partners').doc(id).set(r, { merge: true })
  }

  getPartner = async (id) => {
    return await this.getCustomerRef().collection('partners').doc(id).get()
  }

  getPartners = async () => {
    let r = await this.getCustomerRef().collection('partners').get()
    return r
  }

  /* Membership */
  createMembership = async (membership) => {
    membership.creatorId = this.auth.currentUser.uid
    if (membership.variations) {
      const vs = membership.variations.map((v) => {
        if (!v.varid) v.varid = uuidv4()
        return v
      })
      membership.variations = vs
    }
    return await this.getCustomerRef().collection('membership').add(membership)
  }

  updateMembership = async (membership) => {
    membership.updaterId = this.auth.currentUser.uid
    if (membership.variations) {
      const vs = membership.variations.map((v) => {
        if (!v.varid) v.varid = uuidv4()
        return v
      })
      membership.variations = vs
    }
    return await this.getCustomerRef().collection('membership').doc(membership.id).set(membership, { merge: true })
  }

  deleteMembership = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('membership').doc(id).set(r, { merge: true })
  }

  getMembership = async (id) => {
    return await this.getCustomerRef().collection('membership').doc(id).get()
  }

  getMemberships = async () => {
    let r = await this.getCustomerRef().collection('membership').get()
    return r
  }

  createMembershipResourcePricing = async (config) => {
    config.type = 'membershippricing'
    normalisePackageBandMode(config, false)
    return await this.getCustomerRef().collection('resourcepricings').add(config)
  }

  addMembershipResourcePricingSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'membershippricing'
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  getMembershipResourcePricings = async () => {
    return await this.getCustomerRef().collection('resourcepricings').where('type', '==', 'membershippricing').get()
  }

  getMembershipResourcePricing = async (id) => {
    return await this.getCustomerRef().collection('resourcepricings').doc(id).get()
  }

  deleteMembershipResourcePricings = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resourcepricings').doc(id).set(r, { merge: true })
  }

  updateMembershipResourcePricing = async (config) => {
    config.type = 'membershippricing'
    normalisePackageBandMode(config, true)
    return await this.getCustomerRef().collection('resourcepricings').doc(config.id).set(config, { merge: true })
  }

  /* BenefitCards */
  createBenefitCard = async (benefitcard) => {
    benefitcard.collection = 'activities'
    await this._createBenefitCard(benefitcard)
  }

  createReservationBenefitCard = async (benefitcard) => {
    benefitcard.collection = 'reservations'
    benefitcard.type = 'count'
    await this._createBenefitCard(benefitcard)
  }

  createReservationBenefitTimeCard = async (benefitcard) => {
    benefitcard.collection = 'reservations'
    benefitcard.type = 'time'
    await this._createBenefitCard(benefitcard)
  }


  _createBenefitCard = async (benefitcard) => {
    benefitcard.creatorId = this.auth.currentUser.uid
    benefitcard.state = 'active'
    if (benefitcard.price) {
      benefitcard.price = Math.round(benefitcard.price * 100)
    }
    return await this.getCustomerRef().collection('benefitcards').add(benefitcard)
  }

  updateBenefitCard = async (benefitcard) => {
    benefitcard.collection = 'activities'
    await this._updateBenefitCard(benefitcard)
  }

  updateReservationBenefitCard = async (benefitcard) => {
    benefitcard.collection = 'reservations'
    benefitcard.type = 'count'
    await this._updateBenefitCard(benefitcard)
  }

  updateReservationBenefitTimeCard = async (benefitcard) => {
    benefitcard.collection = 'reservations'
    benefitcard.type = 'time'
    await this._updateBenefitCard(benefitcard)
  }

  _updateBenefitCard = async (benefitcard) => {
    benefitcard.updaterId = this.auth.currentUser.uid
    benefitcard.price = Math.round(benefitcard.price * 100)
    return await this.getCustomerRef().collection('benefitcards').doc(benefitcard.id).set(benefitcard, { merge: true })
  }

  deleteBenefitCard = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('benefitcards').doc(id).set(r, { merge: true })
  }

  getBenefitCard = async (id) => {
    return await this.getCustomerRef().collection('benefitcards').doc(id).get()
  }

  getBenefitCards = async () => {
    let r = await this.getCustomerRef().collection('benefitcards').where('state', '!=', 'deleted').get()
    return r
  }


  createReservationBenefitCardTiming = async (config) => {
    config.type = 'reservationbenefitcard'
    return await this.getCustomerRef().collection('timings').add(config)
  }

  addReservationBenefitCardTimingSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'reservationbenefitcard'
    return await this.getCustomerRef()
      .collection('timings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  getReservationBenefitCardTimings = async () => {
    return await this.getCustomerRef().collection('timings').where('type', '==', 'reservationbenefitcard').get()
  }

  getReservationBenefitCardTiming = async (id) => {
    return await this.getCustomerRef().collection('timings').doc(id).get()
  }

  deleteReservationBenefitCardTiming = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('timings').doc(id).set(r, { merge: true })
  }

  updateReservationBenefitCardTiming = async (config) => {
    config.type = 'reservationbenefitcard'
    return await this.getCustomerRef().collection('timings').doc(config.id).set(config, { merge: true })
  }


  _getPurchases = async (collection) => {
    const purchases = await this.getCustomerRef().collection('purchases').where('collection', '==', collection).get()
    if(!purchases.empty) {
      return purchases.docs.map(doc => ({ ...doc.data(), id: doc.id })).filter(p => p.state !== 'deleted')
    } else {
      return []
    }
  }

  getPurchasedBenefitCards = async (userid) => {
    const purchases = await this._getPurchases('benefitcards')
    if(!userid) {
      return purchases
    }
    return purchases.filter(p => p.userid === userid)
  }

  getPurchasedMemberships = async (userid) => {
    const purchases = await this._getPurchases('memberships')
    if(!userid) {
      return purchases
    }
    return purchases.filter(p => p.userid === userid)
  }



  /* ValueCards - arvokortit, carrying a monetary balance. See docs/features/valuecards.md. */

  // Money fields are stored in CENTS; the form and the container work in euros. `extra` is the
  // bonus value the customer receives on top of what they paid, so it is money like `price` and
  // is converted the same way - it used to be written in euros while price was in cents, which
  // would have been a hundredfold error the first time anything spent it.
  _valueCardToCents = (valuecard) => {
    const toCents = (value) => (typeof value === 'number' && isFinite(value) ? Math.round(value * 100) : 0)
    valuecard.price = toCents(valuecard.price)
    valuecard.extra = toCents(valuecard.extra)
    return valuecard
  }

  createValueCard = async (valuecard) => {
    valuecard.creatorId = this.auth.currentUser.uid
    valuecard.state = 'active'
    this._valueCardToCents(valuecard)
    return await this.getCustomerRef().collection('valuecards').add(valuecard)
  }

  updateValueCard = async (valuecard) => {
    valuecard.updaterId = this.auth.currentUser.uid
    this._valueCardToCents(valuecard)
    return await this.getCustomerRef().collection('valuecards').doc(valuecard.id).set(valuecard, { merge: true })
  }

  deleteValueCard = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('valuecards').doc(id).set(r, { merge: true })
  }

  getValueCard = async (id) => {
    return await this.getCustomerRef().collection('valuecards').doc(id).get()
  }

  getValueCards = async () => {
    let r = await this.getCustomerRef().collection('valuecards').where('state', '!=', 'deleted').get()
    return r
  }

  /* Giftcode */

  createGiftcode = async (giftcode) => {
    giftcode.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('giftcodes').add(giftcode)
  }

  updateGiftcode = async (giftcode) => {
    giftcode.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('giftcodes').doc(giftcode.id).set(giftcode, { merge: true })
  }

  deleteGiftcode = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('giftcodes').doc(id).set(r, { merge: true })
  }

  getGiftcode = async (id) => {
    return await this.getCustomerRef().collection('giftcodes').doc(id).get()
  }

  getGiftcodes = async () => {
    let r = await this.getCustomerRef().collection('giftcodes').get()
    return r
  }

  createGiftcodeResourcePricing = async (config) => {
    config.type = 'giftcodepricing'
    normalisePackageBandMode(config, false)
    return await this.getCustomerRef().collection('resourcepricings').add(config)
  }

  addGiftcodeResourcePricingSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'giftcodepricing'
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  getGiftcodeResourcePricings = async () => {
    return await this.getCustomerRef().collection('resourcepricings').where('type', '==', 'giftcodepricing').get()
  }

  getGiftcodeResourcePricing = async (id) => {
    return await this.getCustomerRef().collection('resourcepricings').doc(id).get()
  }

  deleteGiftcodeResourcePricings = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resourcepricings').doc(id).set(r, { merge: true })
  }

  updateGiftcodeResourcePricing = async (config) => {
    config.type = 'giftcodepricing'
    normalisePackageBandMode(config, true)
    return await this.getCustomerRef().collection('resourcepricings').doc(config.id).set(config, { merge: true })
  }


  /* Contract */
  createContract = async (contract) => {
    contract.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('contract').add(contract)
  }

  updateContract = async (contract) => {
    contract.updaterId = this.auth.currentUser.uid
    delete contract.downloadUrl
    return await this.getCustomerRef().collection('contract').doc(contract.id).set(contract, { merge: true })
  }

  deleteContract = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('contract').doc(id).set(r, { merge: true })
  }

  getContract = async (id) => {
    return await this.getCustomerRef().collection('contract').doc(id).get()
  }

  getContracts = async () => {
    let r = await this.getCustomerRef().collection('contract').get()
    return r
  }

  /* Contract templates */

  // What a contract is made of: the terms text, the covering note, the standard part PDF,
  // the lessor details, and the one piece of real behaviour (validity: fixedterm sets
  // validuntil, openended does not).
  //
  // This replaces the two hardcoded contract types that used to live inline in
  // ReservationModal, and the single config/recurrentterms blob keyed
  // [venue][fixedterm|untilfurthernotice][lang]. A venue can now define as many contract
  // variants as it needs without a code change.

  getContractTemplates = async () => {
    const r = await this.getCustomerRef().collection('contracttemplates').get()
    if (r.empty) return []
    // Filtered client side, NOT with .where('state','!=','deleted'): a != query drops
    // every document that has no state field at all.
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
      .filter((t) => t.state !== 'deleted')
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
  }

  getContractTemplate = async (id) => {
    const r = await this.getCustomerRef().collection('contracttemplates').doc(id).get()
    if (!r.exists) return null
    return { ...r.data(), id: r.id }
  }

  createContractTemplate = async (template) => {
    template.creatorId = this.auth.currentUser.uid
    template.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('contracttemplates').add(template)
  }

  // set({merge:true}) replaces arrays wholesale rather than merging them, which is what we
  // want for venues[] - the editor always sends the whole list.
  updateContractTemplate = async (template) => {
    const payload = { ...template }
    payload.updaterId = this.auth.currentUser.uid
    payload.updated = app.firestore.FieldValue.serverTimestamp()
    delete payload.id
    return await this.getCustomerRef().collection('contracttemplates').doc(template.id).set(payload, { merge: true })
  }

  deleteContractTemplate = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('contracttemplates').doc(id).set(data, { merge: true })
  }

  /* Reporting */
  getReportData = async (period) => {
    return await this.getCustomerReportRef().collection(period + 'bookings').get()
  }

  // useopeningtime: let the backend derive the window per day and per resource from
  // indexes/opentimes instead of the flat starttime..endtime. starttime/endtime are
  // still sent and remain the fallback for dates the index has no entry for - past
  // dates are never backfilled (falcon-a/functions/openingtimes.js floors generation
  // to today), so historical ranges keep today's behaviour.
  getReport = async (startdate, enddate, report, starttime, endtime, useopeningtime) => {
    try {
      this.functions.region = 'europe-west1'
      const callable = this.functions.httpsCallable('reports-get')
      const payload = { startdate, enddate, report, customerid: this.customerid, starttime, endtime }
      if (useopeningtime) payload.useopeningtime = true
      const response = await callable(payload)
      return response
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  // Backend pending — used by Kuukausikooste customer activity KPI.
  // Expected response shape: { data: { rows: [{ month, activeCount, newCount }] } }
  // See docs/functions-to-update.txt for the full spec. Until the report type
  // ships, the callable returns an error and the UI shows its placeholder.
  getCustomerActivityReport = async (startdate, enddate) => {
    return await this.getReport(startdate, enddate, 'customeractivity', '00:00', '23:59')
  }

  // Backend pending — used by Kuukausikooste lukujärjestys heatmap.
  // Expected rows: { dayIndex, hour: 0..23, day, category, occupancy, windowLength }
  // See docs/functions-to-update.txt. Until the report type ships, the
  // callable returns an error and the UI shows its placeholder.
  getHeatmapHourlyReport = async (startdate, enddate, starttime, endtime) => {
    return await this.getReport(startdate, enddate, 'heatmaphourly', starttime || '06:00', endtime || '23:00')
  }


  /* Invoices */

  // Server side replacement for listenInvoicingsRt below, which subscribes to the whole
  // customers/{customerid}/invoicing subtree and filters in the browser. Returns the
  // same payer/rows shape, already filtered to the requested range.
  // Backend pending — see docs/functions-to-update.txt (invoicing-getinvoiceable).
  // Throws when the callable is missing so the caller can fall back.
  getInvoiceable = async (startdate, enddate, venue, seasonbilling) => {
    this.functions.region = 'europe-west1'
    const callable = this.functions.httpsCallable('invoicing-getinvoiceable')
    return await callable({
      customerid: this.customerid,
      startdate,
      enddate,
      venue: venue || null,
      seasonbilling: seasonbilling ? true : false,
    })
  }

  listenInvoicingsRt = () => {
    return this.getRTDBCustomerRef().child('invoicing')
  }

  listenInvoicings = () => {
    return this.getCustomerRef().collection('invoicing')
  }

  getInvoicings = async () => {
    return await this.getCustomerRef().collection('invoicing').get()
  }

  getUserInvoicings = async (userid) => {
    return await this.getCustomerRef().collection('invoicing').doc(userid).get()
  }


  listenInvoices = () => {
    return this.getCustomerRef().collection('invoices')
  }

  getInvoices = async () => {
    return await this.getCustomerRef().collection('invoices').get()
  }

  getInvoicesByDates = async (startdate, enddate) => {
    if (!startdate || !enddate) return await this.getInvoices()
    const _start = moment.utc(startdate).startOf('day')
    const _end = moment.utc(enddate).endOf('day')
    console.log('getInvoicesByDates', _start.format(), _end.format()) 
    const startTimestamp = this.getTimestampFromMoment(_start)
    const endTimestamp = this.getTimestampFromMoment(_end)
    console.log('getInvoicesByDates', startTimestamp, endTimestamp)
    return await this.getCustomerRef()
      .collection('invoices')
      .where('billingdateAsTimestamp', '>=', startTimestamp)
      .where('billingdateAsTimestamp', '<=', endTimestamp)
      .get()
  }

  getUserInvoices = async (userid) => {
    return await this.getCustomerRef().collection('invoices').doc(userid).get()
  }


  /* Message */
  createMessage = async (message) => {
    message.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('messages').add(message)
  }

  sendMessage = async (body, subject, recipient, userid, tags, htmlbody, collection, reservationid) => {
    const message = {
      subject,
      recipient,
      userid,
      tags,
      timestamp: app.firestore.FieldValue.serverTimestamp(),
      creatorId: this.auth.currentUser.uid,
      channel: 'email',
      state: 'processing',
    }
    if (htmlbody) message.htmlbody = htmlbody
    if (body) message.body = body
    if (collection) message.collection = collection
    if (reservationid) message.reservationid = reservationid
    console.log('sendMessage add', message)
    return await this.getCustomerMessageRef().add(message)
    // return await this.getCustomerMessageRef().add({
    //   creatorId: this.auth.currentUser.uid,
    //   body,
    //   subject,
    //   recipient,
    //   channel: 'email',
    //   state: 'processing',
    //   timestamp: app.firestore.FieldValue.serverTimestamp(),
    //   userid,
    //   tags,
    // })
  }

  sendMessageCall = async (body, subject, recipient) => {
    try {
      this.functions.region = 'europe-west1'
      const sendMessage = this.functions.httpsCallable('messaging-sendMessage')
      const response = await sendMessage({ body, subject, recipient, channel: 'email' })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  sendMessages = async (recipientids, subject, body, html, tags) => {
    try {
      this.functions.region = 'europe-west1'
      const sendMessages = this.functions.httpsCallable('messaging-sendMessages')
      const response = await sendMessages({ subject, body, html, recipientids, cid: this.customerid, tags })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }
  /* Group */
  createGroup = (group) => { }

  updateGroup = (group) => { }

  deleteGroup = (id) => { }

  /* Product */



  createProduct = async (product) => {
    product.source = 'internal'
    product.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('products').add(product)
  }

  updateProduct = async (product) => {
    if (product.source === 'internal') {
      product.updaterId = this.auth.currentUser.uid
      // console.log('updateProduct', product, product.id)
      return await this.getCustomerRef().collection('products').doc(product.id).set(product, { merge: true })
    } else {
      product.error = 'Please edit product in webstore/cashier'
      return product
    }
  }

  deleteProduct = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('products').doc(id).set(r, { merge: true })
  }

  getProduct = async (id) => {
    return await this.getCustomerRef().collection('products').doc(id).get()
  }

  getProducts = async () => {
    let r = await this.getCustomerRef().collection('products').where('state', '!=', 'deleted').get()
    return r
  }

  createReservationExtra = async (reservationExtra) => {
    reservationExtra.source = 'internal'
    reservationExtra.state = 'active'
    reservationExtra.creatorId = this.auth.currentUser.uid
    return await this.createProduct(reservationExtra)
  }

  updateReservationExtra = async (reservationExtra) => {
    return await this.updateProduct(reservationExtra)
  }

  deleteReservationExtra = async (id) => {
    return await this.deleteProduct(id)
  }

  getReservationExtra = async (id) => {
    return await this.getProduct(id)
  }

  getReservationExtras = async () => {
    return await this.getProducts()
  }

  getExtrasSchedules = async () => {
    return await this.getSchedules('product')
  }

  createExtrasSchedule = async (schedule) => {
    return await this.createSchedule(schedule, 'product')
  }

  updateExtrasSchedule = async (schedule) => {
    return await this.updateSchedule(schedule)
  }

  deleteExtrasSchedule = async (id) => {
    return await this.deleteSchedule(id)
  }

  /* ShopProduct */

  createShopProduct = async (product) => {
    product.source = 'internal'
    product.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shopproducts').add(product)
  }

  updateShopProduct = async (product) => {
    product.updaterId = this.auth.currentUser.uid
    // console.log('updateProduct', product, product.id)
    return await this.getCustomerRef().collection('shopproducts').doc(product.id).set(product, { merge: true })
  }

  deleteShopProduct = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid, updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() }
    return await this.getCustomerRef().collection('shopproducts').doc(id).set(r, { merge: true })
  }

  getShopProduct = async (id) => {
    return await this.getCustomerRef().collection('shopproducts').doc(id).get()
  }

  getShopProducts = async () => {
    let r = await this.getCustomerRef().collection('shopproducts')/*.where('state', '!=', 'deleted')*/.get()
    return r
  }

  /* ProshopProduct */

  createProshopProduct = async (product) => {
    product.source = 'internal'
    product.creatorId = this.auth.currentUser.uid
    return await this.getCustomerProshopRef().collection('products').add(product)
  }

  updateProshopProduct = async (product) => {
    product.updaterId = this.auth.currentUser.uid
    // console.log('updateProduct', product, product.id)
    return await this.getCustomerProshopRef().collection('products').doc(product.id).set(product, { merge: true })
  }

  deleteProshopProduct = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid, updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() }
    return await this.getCustomerProshopRef().collection('products').doc(id).set(r, { merge: true })
  }

  getProshopProduct = async (id) => {
    return await this.getCustomerProshopRef().collection('products').doc(id).get()
  }

  getProshopProducts = async () => {
    let r = await this.getCustomerProshopRef().collection('products')/*.where('state', '!=', 'deleted')*/.get()
    return r
  }

  /* ProductCategory */

  createProductCategory = async (category) => {
    category.creatorId = this.auth.currentUser.uid
    return await this.getCustomerProshopRef().collection('categories').add(category)
  }

  updateProductCategory = async (category) => {
    category.updaterId = this.auth.currentUser.uid
    return await this.getCustomerProshopRef().collection('categories').doc(category.id).set(category, { merge: true })
  } 

  deleteProductCategory = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerProshopRef().collection('categories').doc(id).set(r, { merge: true })
  }

  getProductCategory = async (id) => {
    return await this.getCustomerProshopRef().collection('categories').doc(id).get()
  }

  getProductCategories = async () => {
    let r = await this.getCustomerProshopRef().collection('categories').get()
    return r
  }

  /* ProductBrand */

  createProductBrand = async (brand) => {
    brand.creatorId = this.auth.currentUser.uid
    return await this.getCustomerProshopRef().collection('brands').add(brand)
  }

  updateProductBrand = async (brand) => {
    brand.updaterId = this.auth.currentUser.uid
    return await this.getCustomerProshopRef().collection('brands').doc(brand.id).set(brand, { merge: true })
  } 

  deleteProductBrand = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerProshopRef().collection('brands').doc(id).set(r, { merge: true })
  }

  getProductBrand = async (id) => {
    return await this.getCustomerProshopRef().collection('brands').doc(id).get()
  }

  getProductBrands = async () => {
    let r = await this.getCustomerProshopRef().collection('brands').get()
    return r
  }

  /* ProshopOrder */

  addProshopOrder = async (order, payer) => {
    order.creatorId = this.auth.currentUser.uid
    order.created = this.getToday()
    order.payer = { id: payer.id }
    order.collection = 'products'
    order.type = 'toinvoice' // inquiery, 
    order.module = 'proshop'
    order.state = 'created' // created, confirmed, paid, delivered, cancelled, deleted
    order.productid = order.id
    delete order.updaterId
    delete order.id
    return await this.getCustomerProshopRef().collection('orders').add(order)
  }

  removePersonProshopOrder = async (orderid) => {
    return await this.getCustomerProshopRef().collection('orders').doc(orderid).update({ state: 'deleted', updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() })
  }

  getProshopOrder = async (id) => {
    return await this.getCustomerProshopRef().collection('orders').doc(id).get()
  }

  getProshopOrders = async () => {
    let r = await this.getCustomerProshopRef().collection('orders').get()
    return r
  }

  // Webshop orders written by the end-user app live in customers/{cid}/proshoporders
  getCustomerProshopOrders = async () => {
    return await this.getCustomerRef().collection('proshoporders').get()
  }

  updateCustomerProshopOrder = async (id, data) => {
    return await this.getCustomerRef().collection('proshoporders').doc(id).set(
      { ...data, updated: app.firestore.FieldValue.serverTimestamp(), updaterId: this.auth.currentUser.uid },
      { merge: true }
    )
  }

  

  /* ProductOrders */

  addProductOrder = async (order, payer) => {
    order.creatorId = this.auth.currentUser.uid
    order.created = this.getToday()
    order.payer = { id: payer.id }
    order.collection = 'products'
    order.state = 'active'
    order.productid = order.id
    delete order.updaterId
    delete order.id
    return await this.getCustomerRef().collection('productorders').add(order)
  }

  removePersonProductOrder = async (orderid) => {
    return await this.getCustomerRef().collection('productorders').doc(orderid).update({ state: 'deleted', updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() })
  }

  /* Schedules */
  createSchedule = async (schedule, type) => {
    schedule.type = type
    schedule.state = 'active'
    schedule.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('schedules').add(schedule)
  }

  updateSchedule = async (schedule) => {
    schedule.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('schedules').doc(schedule.id).set(schedule, { merge: true })
  }

  deleteSchedule = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('schedules').doc(id).set(r, { merge: true })
  }

  getSchedule = async (id) => {
    return await this.getCustomerRef().collection('schedules').doc(id).get()
  }

  getSchedules = async (type) => {
    let r = await this.getCustomerRef().collection('schedules').where('type', '==', type).get()
    return r
  }


  triggerUpdateReservationIndex = async (reservationid) => {
    try {
      this.functions.region = 'europe-west1'
      const triggerIndex = this.functions.httpsCallable('reservations-triggerindexv2')
      const status = await triggerIndex({ reservationid, customerid: this.customerid })
      return status
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  /* Reservation */
  createReservation = async (reservation) => {
    reservation.creatorId = this.auth.currentUser.uid
    reservation.updated = app.firestore.FieldValue.serverTimestamp()
    reservation.via = 'admin'
    try {
      const createdReservation = await this.getCustomerRef().collection('reservations').add(reservation)
      await this.triggerUpdateReservationIndex(createdReservation.id)
      return createdReservation
    } catch (error) {
      return { error: error.message }
    }
  }

  updateReservation = async (reservation) => {
    reservation.updaterId = this.auth.currentUser.uid
    // reservation.t = 2
    reservation.updated = app.firestore.FieldValue.serverTimestamp()
    // console.time('updateReservation')
    try {
      const createdReservation = await this.getCustomerRef()
        .collection('reservations')
        .doc(reservation.id)
        .set(reservation, { merge: true })
      // console.timeEnd('updateReservation')

      // console.time('triggerUpdateReservationIndex')
      await this.triggerUpdateReservationIndex(reservation.id)
      // console.timeEnd('triggerUpdateReservationIndex')

      return createdReservation
    } catch (error) {
      return { error: error.message }
    }
  }

  _updateReservationState = async (id, state) => {
    let r = { state: state }
    r.creatorId = this.auth.currentUser.uid
    r.updaterId = this.auth.currentUser.uid
    r.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('reservations').doc(id).set(r, { merge: true })
  }

  _cancelReservation = async (id) => {
    let r = { state: 'cancelled' }
    r.creatorId = this.auth.currentUser.uid
    r.updaterId = this.auth.currentUser.uid
    r.updated = app.firestore.FieldValue.serverTimestamp()
    try {
      const updatedReservation = await this.getCustomerRef()
        .collection('reservations')
        .doc(r.id)
        .set(r, { merge: true })
      await this.triggerUpdateReservationIndex(id)
      return updatedReservation
    } catch (error) {
      return { error: error.message }
    }
    // return await this.getCustomerRef()
    //   .collection('reservations')
    //   .doc(id)
    //   .set(r, { merge: true })
  }

  cancelMultiReservation = async (occurencies, subject, body) => {
    try {
      this.functions.region = 'europe-west1'
      const cancelAdminMultiReservations = this.functions.httpsCallable('reservations-cancelAdminMultiReservations')
      const status = await cancelAdminMultiReservations({ occurencies, subject, body, cid: this.customerid })
      return status
    } catch (error) {
      return { error: error.message }
    }
  }

  setOnSaleReservation = async (id) => {
    return this._updateReservationState(id, 'onsale')
  }

  cancelReservation = async (id) => {
    return this._updateReservationState(id, 'cancelled')
  }

  getReservation = async (id) => {
    return await this.getCustomerRef().collection('reservations').doc(id).get()
  }

  getReservations = async () => {
    let r = await this.getCustomerRef().collection('reservations').get()
    return r
  }

  addSaldoPayment = async (payment) => {
    try {
      payment.q = 'addSaldoPayment'
      payment.cid = this.customerid
      const apicall = this.getAdminAPICall()
      const response = await apicall(payment)
      return response
    }
    catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  addStoragePayment = async (payment) => {
    try {
      payment.q = 'addStoragePayment'
      payment.cid = this.customerid
      const apicall = this.getAdminAPICall()
      const response = await apicall(payment)
      return response
    }
    catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  addPayment = async (reservationid, payment) => {
    try {
      const data = { reservationid, payment, cid: this.customerid, q: 'addPayment' }
      const apicall = this.getAdminAPICall()
      const response = await apicall(data)
      return response
    }
    catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  listenReservations = () => {
    return this.getCustomerRef().collection('reservations')
  }

  listenReservation = (id) => {
    return this.getCustomerRef().collection('reservations').doc(id)
  }


  // getBookings = async (date) => {
  //   // console.log(date)
  //   const _bookings = await this.db.ref('bookings/' + this.customerid + '/' + date).once('value')
  //   if (_bookings.exists()) {
  //     const bookings = Object.keys(_bookings.val()).map((key) => _bookings.val()[key])
  //     return bookings
  //   } else {
  //     return []
  //   }
  // }

  // listenBookings = (date) => {
  //   // return this.db.ref('bookings/' + this.customerid + '/' + date).orderByChild('data/state').equalTo('reserved')
  //   return this.db.ref('bookings/' + this.customerid + '/' + date)
  // }

  listenBookingStore = (date) => {
    return this.getCustomerBookingIndexRef().doc(date)
  }

  listenBookingIndex = (date) => {
    return this.getCustomerBookingIndexingRef().collection(date)
  }

  listenFreetimes = () => {
    return this.db.ref('freetimes/' + this.customerid)
  }

  // Same node, one day at a time. listenFreetimes() above subscribes to the WHOLE subtree, and
  // a 'value' listener re-transfers all of it on every change anywhere beneath it - the venue's
  // entire future downloaded to render one day. CustomerTimesCalendarContainer asks for a key
  // range instead.
  //
  // Keys are 10-digit epoch seconds and orderByKey compares them as strings, so lexicographic
  // order equals numeric order here. Bounds come from dayKeyRange() in utils/freetimes.js.
  listenFreetimesRange = (startKey, endKey) => {
    return this.db
      .ref('freetimes/' + this.customerid)
      .orderByKey()
      .startAt(String(startKey))
      .endAt(String(endKey))
  }

  // The first free-time slot at or after a key, as a one-off read of a SINGLE child. Lets a view
  // that found nothing for one day say which it is - "nothing today" or "this venue has no
  // free-time data at all" - instead of hedging, and without reading the whole subtree to
  // find out.
  getNextFreetime = async (fromKey) => {
    return await this.db
      .ref('freetimes/' + this.customerid)
      .orderByKey()
      .startAt(String(fromKey))
      .limitToFirst(1)
      .once('value')
  }

  listenLights = () => {
    return this.db.ref('test/lights/' + this.customerid)
  }

  listenInfoTexts = () => {
    return this.db.ref('infotexts/' + this.customerid)
  }

  /* Organisation */
  registerOrganisation = async (organisation) => {
    organisation.customerid = this.customerid
    this.db.ref('regbusiness').push(organisation)
  }

  createCustomerOrganisation = async (organisation) => {
    return await this.getCustomerRef().collection('organisations').add(organisation)
  }

  storeContacts = async (contacts, organisationid) => {
    if (organisationid) {
      return await this.getCustomerRef().collection('organisations').doc(organisationid).set(contacts, { merge: true })
    } else {
      alert('No organisation selected')
    }
  }

  updateOrganisation = async (organisation) => {
    delete organisation.customers
    return await this.getCustomerRef()
      .collection('organisations')
      .doc(organisation.id)
      .set(organisation, { merge: true })
  }

  deleteOrganisation = async (id) => {
    let r = { id: id, state: 'deleted' }
    return await this.updateOrganisation(r)
  }

  listenOrganisations = () => {
    return this.getCustomerRef().collection('organisations')
  }

  listenOrganisationsRT = () => {
    return this.db.ref('organisations/')
  }

  // ** Configs **

  addOpeningTimes = (config) => {
    return this.getCustomerRef().collection('config').doc('openingtimes').set(config, { merge: true })
  }

  getOpeningTimes = () => {
    return this.getCustomerRef().collection('config').doc('openingtimes')
  }

  // Reads the current openingtimes doc, replaces (or appends) the rule with a
  // matching id, recomputes the legacy `defaults` projection from the highest-
  // priority always-on rule, and writes the whole doc back. The legacy field
  // stays in sync so the backend index maintainer (indexes/opentimes/...)
  // keeps producing valid per-day entries while we transition.
  saveOpeningHoursRule = async (rule, { flattenRulesToDefaults }) => {
    const ref = this.getCustomerRef().collection('config').doc('openingtimes')
    const snap = await ref.get()
    const current = snap.exists ? snap.data() : {}
    const rules = Array.isArray(current.rules) ? current.rules.slice() : []
    const id = rule.id || `rule-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const next = { ...rule, id }
    const idx = rules.findIndex((r) => r && r.id === id)
    if (idx >= 0) {
      rules[idx] = next
    } else {
      rules.push(next)
    }
    const defaults = flattenRulesToDefaults(rules)
    return ref.set({ ...current, rules, defaults }, { merge: true })
  }

  deleteOpeningHoursRule = async (id, { flattenRulesToDefaults }) => {
    const ref = this.getCustomerRef().collection('config').doc('openingtimes')
    const snap = await ref.get()
    const current = snap.exists ? snap.data() : {}
    const rules = (Array.isArray(current.rules) ? current.rules : []).filter((r) => r && r.id !== id)
    const defaults = flattenRulesToDefaults(rules)
    return ref.set({ ...current, rules, defaults }, { merge: true })
  }

  getDayOpeningTimes = (day) => {
    const index = moment(day, 'YYYY-MM-DD').format('YYYYMMDD')
    console.log(index)
    return this.firestore.collection('indexes').doc('opentimes').collection(this.customerid).doc(index).get()
  }

  addResourceCategories = (config) => {
    return this.getCustomerRef().collection('config').doc('resourcecategories').set(config, { merge: true })
  }

  getResourceCategories = () => {
    return this.getCustomerRef().collection('config').doc('resourcecategories')
  }

  addMessageTemplates = (config) => {
    return this.getCustomerRef().collection('config').doc('messagetemplates').set(config, { merge: true })
  }

  getMessageTemplates = () => {
    return this.getCustomerRef().collection('config').doc('messagetemplates')
  }

  getAdminNotification = () => {
    return this.getCustomerRef().collection('config').doc('adminnotification')
  }

  addAdminNotification = (config) => {
    return this.getCustomerRef().collection('config').doc('adminnotification').set(config, { merge: true })
  }


  addColorCategories = (config) => {
    return this.getCustomerRef().collection('config').doc('colorcategories').set(config, { merge: true })
  }

  getColorCategories = () => {
    return this.getCustomerRef().collection('config').doc('colorcategories')
  }

  getCustomerExtras = () => {
    return this.getCustomerRef().collection('config').doc('customerextras')
  }

  addCustomerExtras = (config) => {
    return this.getCustomerRef().collection('config').doc('customerextras').set(config, { merge: true })
  }

  getTerms = () => {
    return this.getCustomerRef().collection('config').doc('terms')
  }

  addTerms = (config) => {
    return this.getCustomerRef().collection('config').doc('terms').set(config, { merge: true })
  }

  getRecurrentTerms = () => {
    return this.getCustomerRef().collection('config').doc('recurrentterms')
  }

  addRecurrentTerms = (config) => {
    return this.getCustomerRef().collection('config').doc('recurrentterms').set(config, { merge: true })
  }

  getActivityConfig = () => {
    return this.getCustomerRef().collection('config').doc('activity')
  }

  addActivityConfig = (config) => {
    return this.getCustomerRef().collection('config').doc('activity').set(config, { merge: true })
  }

  getConfig = (configName) => {
    return this.getCustomerRef().collection('config').doc(configName)
  }

  addConfig = async (configName, config) => {
    return await this.getCustomerRef().collection('config').doc(configName).set(config, { merge: true })
  }


  addResourceConfigs = (config) => {
    if (!config.id) config.id = this.getRandomId()
    return this.getCustomerRef()
      .collection('config')
      .doc('resourceconfigs')
      .collection('config')
      .doc(config.id)
      .set(config, { merge: true })
  }

  getResourceConfigs = () => {
    return this.getCustomerRef().collection('config').doc('resourceconfigs').collection('config').get()
  }

  createResourcePricing = async (config) => {
    config.type = 'resourcepricing'
    normalisePackageBandMode(config, false)
    return await this.getCustomerRef().collection('resourcepricings').add(config)
  }

  addResourcePricingSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'resourcepricing'
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  getResourcePricings = async () => {
    return await this.getCustomerRef().collection('resourcepricings').where('type', '==', 'resourcepricing').get()
  }

  getResourcePricing = async (id) => {
    return await this.getCustomerRef().collection('resourcepricings').doc(id).get()
  }

  deleteResourcePricings = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resourcepricings').doc(id).set(r, { merge: true })
  }

  updateResourcePricing = async (config) => {
    config.type = 'resourcepricing'
    normalisePackageBandMode(config, true)
    return await this.getCustomerRef().collection('resourcepricings').doc(config.id).set(config, { merge: true })
  }

  getReservationPrices = async (slot, personid) => {
    try {
      this.functions.region = 'europe-west1'
      // Test deployment for now. Switch to 'reservations-getpricev4' once the functions are
      // deployed to production - and rename the resourceId casing notes that cite this name.
      const getprice = this.functions.httpsCallable('testreservations-getpricev4')
      const prices = await getprice({ slot, personid, customerid: this.customerid })
      return prices
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  isConflict = async (slots) => {
    // console.log('Checking conflicts')
    try {
      this.functions.region = 'europe-west1'
      const isConflict = this.functions.httpsCallable('redis-isconflict')
      const conflictData = await isConflict({ slots, customerid: this.customerid })
      // console.log('got conflicts', conflictData)
      return conflictData.data
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  isConflictv2 = async (slots) => {
    // console.log('Checking conflicts')
    try {
      this.functions.region = 'europe-west1'
      const isConflict = this.functions.httpsCallable('redisv2-isconflictv2')
      // const isConflict = this.functions.httpsCallable('redisv2-isconflict')
      // const isConflict = this.functions.httpsCallable('redis-isconflict')
      const conflictData = await isConflict({ slots, customerid: this.customerid })
      // console.log('got conflicts', conflictData)
      return conflictData.data
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }


  // SALDO

  addSaldoTransaction = async (userid, type, amount, description, wallet, venue) => {
    try {
      const transaction = { type, amount, description }
      this.functions.region = 'europe-west1'
      const addTransaction = this.functions.httpsCallable('users-addsaldotransaction')
      const addedTransaction = await addTransaction({ userid, customerid: this.customerid, transaction, wallet, venue })
      return addedTransaction
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getPersonSaldo = async (userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getTransactions = this.functions.httpsCallable('users-getsaldotransactions')
      const transactions = await getTransactions({ userid, customerid: this.customerid })
      return transactions
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  addStorageTransaction = async (userid, type, amount, description, wallet, venue) => {
    try {
      const transaction = { type, amount, description }
      this.functions.region = 'europe-west1'
      const addTransaction = this.functions.httpsCallable('users-addstoragetransaction')
      const addedTransaction = await addTransaction({ userid, customerid: this.customerid, transaction, wallet, venue })
      return addedTransaction
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getPersonStorage = async (userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getTransactions = this.functions.httpsCallable('users-getstoragetransactions')
      const transactions = await getTransactions({ userid, customerid: this.customerid })
      return transactions
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getOrganisationSaldo = async (organisationid) => {
    return await this.getCustomerRef().collection('organisations').doc(organisationid).collection('saldo').get()
  }

  getOrganisationStorage = async (organisationid) => {
    return await this.getCustomerRef().collection('organisations').doc(organisationid).collection('storage').get()
  }


  getPersonDetailsCommand = async (command, userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getPersonDetails = this.functions.httpsCallable('users-persondetails')
      // const getPersonDetails = this.functions.httpsCallable('users-testpersondetails')
      const data = { command, customerid: this.customerid }
      if (userid) data.userid = userid
      return await getPersonDetails(data)
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }


  // PERSON ACL

  getAcls = async (userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getAcls = this.functions.httpsCallable('users-getAcls')
      const acls = await getAcls({ userid, cid: this.customerid })
      return acls
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }


  setAcl = async ({ userid, acl }) => {
    try {
      this.functions.region = 'europe-west1'
      const setAcl = this.functions.httpsCallable('users-setAcl')
      const acls = await setAcl({ userid, cid: this.customerid, acl })
      return acls
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getAclsPd = async (userid) => {
    return await this.getPersonDetailsCommand('getAcls', userid)
  }

  // PERSON ACCESSCODE

  getPersonPin = async (userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getPersonPin = this.functions.httpsCallable('users-getpin')
      const pin = await getPersonPin({ userid, customerid: this.customerid })
      return pin
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getPersonPinPd = async (userid) => {
    return await this.getPersonDetailsCommand('getpin', userid)
  }

  // PERSON MESSAGES

  getPersonMessages = async (userid) => {
    try {
      const _me = await this.firestore.collection('messages').doc(this.customerid).collection('active').where('userid', '==', userid).get()
      if (!_me.empty) {
        return _me.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
      } else {
        return []
      }
    } catch (error) {
      console.error(error)
      return []
    }
  }

  getPersonMessagesPd = async (userid) => {
    return await this.getPersonDetailsCommand('getmessages', userid)
  }

  // PERSON BENEFITCARDS

  getPersonBenefitCards = async (userid) => {
    try {
      this.functions.region = 'europe-west1'
      const getPersonBenefitCards = this.functions.httpsCallable('users-getbenefitcards')
      const benefitcards = await getPersonBenefitCards({ userid, customerid: this.customerid })
      return benefitcards
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  addNewPersonBenefitCard = async (userid, benefitcardid, note) => {
    try {
      this.functions.region = 'europe-west1'
      // const addPersonBenefitCard = this.functions.httpsCallable('users-addbenefitcard')
      const addPersonBenefitCard = this.functions.httpsCallable('users-addbenefitcardsync')
      const benefitcard = await addPersonBenefitCard({ userid, customerid: this.customerid, benefitcardid, note })
      return benefitcard
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  addNewPersonTimedBenefitCard = async (userid, benefitcardid, note, start, end, permanent) => {
    try {
      this.functions.region = 'europe-west1'
      // const addPersonBenefitCard = this.functions.httpsCallable('users-addbenefitcard')
      const addPersonBenefitCard = this.functions.httpsCallable('users-addbenefitcardsync')
      const benefitcard = await addPersonBenefitCard({ userid, customerid: this.customerid, benefitcardid, note, start, end, permanent })
      return benefitcard
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }


  updatePersonBenefitCard = async (userid, id, remaining) => {
    try {
      this.functions.region = 'europe-west1'
      // const updatePersonBenefitCard = this.functions.httpsCallable('users-updatebenefitcard')
      const updatePersonBenefitCard = this.functions.httpsCallable('users-updatebenefitcardsync')
      const benefitcard = await updatePersonBenefitCard({ userid, customerid: this.customerid, id, remaining })
      return benefitcard
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getPersonBenefitCardsPd = async (userid) => {
    return await this.getPersonDetailsCommand('getbenefitcards', userid)
  }

  getCustomerBenefitCardsPd = async () => {
    return await this.getPersonDetailsCommand('benefitcards')
  }

  // return this.getCustomerRef().collection('messages').doc(this.customerid).collection('config').get()
  // getPersonMessages = async (userid) => {
  //   try {
  //     this.functions.region = 'europe-west1'
  //     const getPersonMessages = this.functions.httpsCallable('users-getmessages')
  //     const messages = await getPersonMessages({ userid, customerid: this.customerid })
  //     return messages
  //   } catch (error) {
  //     console.error(error)
  //     return { error: true, message: error.message }
  //   }
  // }


  // REMOVE PERSON CUSTOMERSHIP



  // PERSON MEMBERSHIPS

  addNewMembership = async (userid, membershipid, start, end, note, permanent) => {
    try {
      const membership = { membershipid, start, end, note, permanent }
      this.functions.region = 'europe-west1'
      // const addMembership = this.functions.httpsCallable('users-addmembership')
      const addMembership = this.functions.httpsCallable('users-addmembershipsync')
      const addedMembership = await addMembership({ userid, customerid: this.customerid, membership })
      return addedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  updateUserMembership = async (userid, id, start, end, note, permanent) => {
    try {
      // console.log('updateMembership ff','userid', userid, 'id', id, 'start', start, 'end', end, 'note', note, 'permanent', permanent)      
      const membership = { start, end, note, permanent }
      this.functions.region = 'europe-west1'
      // const updateMembership = this.functions.httpsCallable('users-updatemembership')
      const updateMembership = this.functions.httpsCallable('users-updatemembershipsync')
      const updatedMembership = await updateMembership({ userid, customerid: this.customerid, id, membership })
      return updatedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  terminateMembership = async (userid, id, end, permanent) => {
    try {
      const membership = { end, permanent }
      this.functions.region = 'europe-west1'
      // const updateMembership = this.functions.httpsCallable('users-updatemembership')
      const updateMembership = this.functions.httpsCallable('users-updatemembershipsync')
      const updatedMembership = await updateMembership({ userid, customerid: this.customerid, id, membership })
      return updatedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getPersonMemberships = async (userid, serverfilter) => {
    try {
      this.functions.region = 'europe-west1'
      const getMemberships = this.functions.httpsCallable('users-getmemberships')
      if (serverfilter) {
        const memberships = await getMemberships({ userid, customerid: this.customerid, serverfilter })
        return memberships
      } else {
        const memberships = await getMemberships({ userid, customerid: this.customerid })
        return memberships
      }
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getOrganisationMemberships = async (organisationid) => {
    try {
      this.functions.region = 'europe-west1'
      const getMemberships = this.functions.httpsCallable('organisations-getmemberships')
      const memberships = await getMemberships({ organisationid, customerid: this.customerid })
      return memberships
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }


  addNewOrganisationMembership = async (organisationid, membershipid, start, end, note, permanent) => {
    try {
      const membership = { membershipid, start, end, note, permanent }
      this.functions.region = 'europe-west1'
      const addMembership = this.functions.httpsCallable('organisations-addmembership')
      const addedMembership = await addMembership({ organisationid, customerid: this.customerid, membership })
      return addedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  updateOrganisationMembership = async (organisationid, id, start, end, note, permanent) => {
    try {
      // console.log('updateMembership ff','userid', userid, 'id', id, 'start', start, 'end', end, 'note', note, 'permanent', permanent)      
      const membership = { start, end, note, permanent }
      this.functions.region = 'europe-west1'
      const updateMembership = this.functions.httpsCallable('organisations-updatemembership')
      const updatedMembership = await updateMembership({ organisationid, customerid: this.customerid, id, membership })
      return updatedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  terminateOrganisationMembership = async (organisation, id, end, permanent) => {
    try {
      const membership = { end, permanent }
      this.functions.region = 'europe-west1'
      const updateMembership = this.functions.httpsCallable('organisations-updatemembership')
      // organisationid, not userid: this call sent the organisation under the key `userid`,
      // unlike every other organisations-* call. Both keys are sent for now in case the
      // backend has been reading the wrong one all along - drop `userid` once confirmed.
      const updatedMembership = await updateMembership({
        organisationid: organisation,
        userid: organisation,
        customerid: this.customerid,
        id,
        membership,
      })
      return updatedMembership
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }



  getPersonCDataPd = async (id) => {
    return await this.getPersonDetailsCommand('getcdata', id)
  }

  getPersonMembershipsPd = async (userid) => {
    return await this.getPersonDetailsCommand('getmemberships', userid)
  }

  getCustomerMembershipsPd = async () => {
    return await this.getPersonDetailsCommand('memberships')
  }

  getPersonProductOrdersPd = async (userid) => {
    return await this.getPersonDetailsCommand('productorders', userid)
  }

  // Returns activities the person participates in or is queued for.
  // Backed by users-persondetails cloud function with command 'getactivities'.
  // See docs/functions-to-update.txt for the expected response shape.
  getPersonActivitiesPd = async (userid) => {
    return await this.getPersonDetailsCommand('getactivities', userid)
  }

  getPersonDetails = async (userid) => {
    return await this.getPersonDetailsCommand('getuserdetails', userid)
  }

  getInvoicingFake = async () => {
    return Promise.resolve({
      empty: false, docs: [
        { data: () => { return { name: 'default' } }, id: '1' },
        { data: () => { return { name: 'meilahdenpalloilu' } }, id: '2' },
      ]
    })
  }
  getVenues = async () => {
    if (this.customerid.indexOf('meilahdenliikunta') > -1) return await this.getInvoicingFake()
    return await this.getCustomerRef().collection('venues').get()
  }

  // getVenues = () => {
  //   if (this.customerid && this.customerid.startsWith('tali')) {
  //     return ['tali', 'taivallahti']
  //   } else if (this.customerid && this.customerid.startsWith('evs')) {
  //     return ['aktia', 'martinmäki']
  //   } else {
  //     return []
  //   }
  // }

  // UTILS

  getDiffs = async (id) => {
    const _diffLeaf = await this.db.ref('logs/' + this.customerid + '/' + id).once('value')
    if (_diffLeaf.exists()) {
      // const bookings = Object.keys(_bookings.val()).map((key) => _bookings.val()[key])
      return _diffLeaf.val()
    } else {
      return {}
    }
  }

  getPasswordResetLink = async (email) => {
    try {
      this.functions.region = 'europe-west1'
      const getPasswordReset = this.functions.httpsCallable('users-generatePasswordResetLink')
      const link = await getPasswordReset({ email })
      return link
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  findOrganisationFromPrh = async (businessId, name) => {
    try {
      this.functions.region = 'europe-west1'
      // const getBusiness = this.functions().httpsCallable('organisation-getBusiness')
      const getBusiness = this.functions.httpsCallable('organisations-getBusiness')
      const business = await getBusiness({ businessid: businessId, name: name })
      return business
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  getOrganisationFromPrhById = async (businessId) => {
    try {
      this.functions.region = 'europe-west1'
      const getBusiness = this.functions.httpsCallable('organisations-getBusinessByBusinessId')
      const business = await getBusiness({ businessid: businessId })
      return business
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  // CACHE

  searchPerson = (pattern) => {
    // console.log(pattern)
    // let results = this.cache.searchPersons(pattern)
    // console.log(results)
    return this.cache.searchPersons(pattern)
  }

  updatePersonCache = (persons) => {
    // console.log('updatePersonCache', persons)
    this.cache.updatePersons(persons)
  }

  searchOrganisations = (pattern) => {
    // console.log(pattern)
    return this.cache.searchOrganisations(pattern)
  }

  updateOrganisationsCache = (organisations) => {
    this.cache.updateOrganisations(organisations)
  }

  searchOrganisations = (pattern) => {
    // console.log(pattern)
    return this.cache.searchOrganisations(pattern)
  }

  updateCombinedCache = (organisations, persons) => {
    this.cache.updateCombined(organisations, persons)
  }

  searchCombined = (pattern) => {
    // console.log(pattern)
    return this.cache.searchCombined(pattern)
  }

  isCacheValid = () => {
    return this.cache.isValid()
  }

  // Action Messages
  listenActionMessages = () => {
    // console.log('listening /actionmessages/' + this.customerid)
    return this.db.ref('actionmessages/' + this.customerid + '/').orderByKey() //.orderByChild('created')
  }

  // Shareholders
  createShareholderSlot = async (shareholderSlot) => {
    shareholderSlot.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shareholders').add(shareholderSlot)
  }

  updateShareholderSlot = async (shareholderSlot) => {
    shareholderSlot.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef()
      .collection('shareholders')
      .doc(shareholderSlot.id)
      .set(shareholderSlot, { merge: true })
  }

  _updateShareholderSlotState = async (id, state) => {
    let r = { state: state }
    r.creatorId = this.auth.currentUser.uid

    return await this.getCustomerRef().collection('shareholders').doc(id).set(r, { merge: true })
  }

  setOnSaleShareholderSlot = async (id) => {
    return this._updateShareholderSlotState(id, 'onsale')
  }

  cancelShareholderSlot = async (id) => {
    return this._updateShareholderSlotState(id, 'cancelled')
  }

  getShareholderSlot = async (id) => {
    return await this.getCustomerRef().collection('shareholders').doc(id).get()
  }

  getShareholderSlots = async () => {
    let r = await this.getCustomerRef().collection('shareholders').get()
    return r
  }

  // Share Registry - Classes
  getShareClasses = async () => {
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('classes').get()
  }

  createShareClass = async (data) => {
    data.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('classes').add(data)
  }

  updateShareClass = async (data) => {
    data.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('classes').doc(data.id).set(data, { merge: true })
  }

  deleteShareClass = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('classes').doc(id).set(r, { merge: true })
  }

  // Share Registry - Templates
  getShareTemplates = async () => {
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('templates').get()
  }

  createShareTemplate = async (data) => {
    data.creatorId = this.auth.currentUser.uid
    data.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('templates').add(data)
  }

  updateShareTemplate = async (data) => {
    data.updaterId = this.auth.currentUser.uid
    data.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('templates').doc(data.id).set(data, { merge: true })
  }

  deleteShareTemplate = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('templates').doc(id).set(r, { merge: true })
  }

  // Share Registry - Shares
  getShares = async () => {
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('shares').get()
  }

  createShare = async (data) => {
    data.creatorId = this.auth.currentUser.uid
    if (data.transactions) {
      data.transactions = data.transactions.map((t) => {
        if (!t.id) t.id = this.firestore.collection('_').doc().id
        return t
      })
    }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('shares').add(data)
  }

  updateShare = async (data) => {
    data.updaterId = this.auth.currentUser.uid
    if (data.transactions) {
      data.transactions = data.transactions.map((t) => {
        if (!t.id) t.id = this.firestore.collection('_').doc().id
        return t
      })
    }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('shares').doc(data.id).set(data, { merge: true })
  }

  deleteShare = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('shares').doc(id).set(r, { merge: true })
  }

  createSharesBatch = async (firstNumber, lastNumber, template) => {
    const count = lastNumber - firstNumber + 1
    if (count < 1 || count > 500) throw new Error('Batch size must be between 1 and 500')
    const batch = this.firestore.batch()
    const collRef = this.getCustomerRef().collection('shareholders').doc('registry').collection('shares')
    for (let i = firstNumber; i <= lastNumber; i++) {
      const docRef = collRef.doc()
      batch.set(docRef, {
        shareNumber: i,
        classId: template.classId || '',
        templateId: template.id || '',
        currentOwnerId: '',
        properties: template.properties ? JSON.parse(JSON.stringify(template.properties)) : [],
        transactions: [],
        creatorId: this.auth.currentUser.uid,
        created: app.firestore.FieldValue.serverTimestamp(),
      })
    }
    return await batch.commit()
  }

  updateSharesBatchTransaction = async (shareIds, transaction) => {
    if (!shareIds || shareIds.length === 0) throw new Error('No shares selected')
    if (shareIds.length > 500) throw new Error('Batch size must not exceed 500')
    if (!transaction.id) transaction.id = this.firestore.collection('_').doc().id
    if (!transaction.dealId) transaction.dealId = this.firestore.collection('_').doc().id
    transaction.dealShareCount = shareIds.length
    const batch = this.firestore.batch()
    const collRef = this.getCustomerRef().collection('shareholders').doc('registry').collection('shares')
    const newOwnerId = transaction.type === 'buy' ? transaction.toId : transaction.fromId
    shareIds.forEach((id) => {
      const docRef = collRef.doc(id)
      const update = {
        transactions: app.firestore.FieldValue.arrayUnion(transaction),
        updaterId: this.auth.currentUser.uid,
        updated: app.firestore.FieldValue.serverTimestamp(),
      }
      if (newOwnerId) update.currentOwnerId = newOwnerId
      batch.update(docRef, update)
    })
    return await batch.commit()
  }

  updateSharesBatchProperties = async (shareIds, properties) => {
    if (!shareIds || shareIds.length === 0) throw new Error('No shares selected')
    if (shareIds.length > 500) throw new Error('Batch size must not exceed 500')
    const batch = this.firestore.batch()
    const collRef = this.getCustomerRef().collection('shareholders').doc('registry').collection('shares')
    shareIds.forEach((id) => {
      const docRef = collRef.doc(id)
      batch.update(docRef, {
        properties,
        updaterId: this.auth.currentUser.uid,
        updated: app.firestore.FieldValue.serverTimestamp(),
      })
    })
    return await batch.commit()
  }

  // Share Registry - Certificates
  getShareCertificates = async () => {
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('certificates').get()
  }

  createShareCertificate = async (data) => {
    data.creatorId = this.auth.currentUser.uid
    data.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('certificates').add(data)
  }

  updateShareCertificate = async (data) => {
    data.updaterId = this.auth.currentUser.uid
    data.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('certificates').doc(data.id).set(data, { merge: true })
  }

  deleteShareCertificate = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('certificates').doc(id).set(r, { merge: true })
  }

  updateSharesBatchCertificate = async (shareIds, certificateId) => {
    if (!shareIds || shareIds.length === 0) throw new Error('No shares selected')
    if (shareIds.length > 500) throw new Error('Batch size must not exceed 500')
    const batch = this.firestore.batch()
    const collRef = this.getCustomerRef().collection('shareholders').doc('registry').collection('shares')
    shareIds.forEach((id) => {
      const docRef = collRef.doc(id)
      batch.update(docRef, {
        certificateId,
        updaterId: this.auth.currentUser.uid,
        updated: app.firestore.FieldValue.serverTimestamp(),
      })
    })
    return await batch.commit()
  }

  // Share Registry - Holders
  getShareholders = async () => {
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('holders').get()
  }

  createShareholder = async (data) => {
    data.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('holders').add(data)
  }

  updateShareholder = async (data) => {
    data.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('holders').doc(data.id).set(data, { merge: true })
  }

  deleteShareholder = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('shareholders').doc('registry').collection('holders').doc(id).set(r, { merge: true })
  }

  sendConfirmation = async (newemail, userid, language) => {
    try {
      this.functions.region = 'europe-west1'
      const createConfirmation = this.functions.httpsCallable('users-createConfirmationAdmin')
      const response = await createConfirmation({ newemail, cid: this.customerid, userid, language })
      return response
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  invoiceAction = async (userid, rows, action, markasinvoiced, invoicer, billingdate) => {
    try {
      this.functions.region = 'europe-west1'
      const invoiceAction = this.functions.httpsCallable('invoicing-invoiceAction')
      // console.log({ userid, customerid: this.customerid, rows, action, markasinvoiced, invoicer })
      const response = await invoiceAction({ userid, customerid: this.customerid, rows, action, markasinvoiced, invoicer, billingdate })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  invoiceBatchAction = async (rows, action, markasinvoiced, invoicer, billingdate) => { // invoicer === venue
    try {
      this.functions.region = 'europe-west1'
      const invoiceBatchAction = this.functions.httpsCallable('invoicing-invoiceBatchActionV2')
      // console.log({customerid: this.customerid, rows, action, markasinvoiced, invoicer })
      const response = await invoiceBatchAction({ customerid: this.customerid, rows, action, markasinvoiced, invoicer, billingdate })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  invoiceCombinedAction = async (rows, payer, invoicer, billingdate, addoriginalusertorow, billingreference) => {
    try {
      this.functions.region = 'europe-west1'
      const createInvoiceAction = this.functions.httpsCallable('invoicing-createInvoiceAction')
      const response = await createInvoiceAction({ customerid: this.customerid, rows, payer, invoicer, billingdate, addoriginalusertorow, billingreference })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  invoiceCreateAction = async (rows, action, markasinvoiced, invoicer, billingdate) => {
    console.log('invoiceCreateAction', rows, action, markasinvoiced, invoicer, billingdate)
    try {
      this.functions.region = 'europe-west1'
      const invoiceCreate = this.functions.httpsCallable('invoicing-invoiceCreate')
      const response = await invoiceCreate({ customerid: this.customerid, rows, action, markasinvoiced, invoicer, billingdate })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  batchAddSaldos = async (userid, rows) => {
    try {
      this.functions.region = 'europe-west1'
      const _batchAddSaldos = this.functions.httpsCallable('invoicing-batchAddSaldos')
      // console.log({customerid: this.customerid, rows, action, markasinvoiced, invoicer })
      const response = await _batchAddSaldos({ customerid: this.customerid, rows, userid })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  copyRecurrectsBatchAction = async (ids, startdate, enddate, overrideminus, overrideminuspercent) => {
    try {
      // console.log(ids, startdate, enddate, overrideminus)
      this.functions.region = 'europe-west1'
      const copyBatchAction = this.functions.httpsCallable('reservations-copyRecurrings')
      // console.log({customerid: this.customerid, ids, startdate, enddate, overrideminus })
      const response = await copyBatchAction({ customerid: this.customerid, ids, startdate, enddate, overrideminus, overrideminuspercent })
      // console.log('response', response)
      return response.data
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  removeCustomership = async (userid) => {
    try {
      // console.log('Removing', userid)
      this.functions.region = 'europe-west1'
      const deleteCustomership = this.functions.httpsCallable('users-deletecustomership')
      const response = await deleteCustomership({ userid, customerid: this.customerid })
      return response
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  /* Infotext */
  createInfotext = async (infotext) => {
    infotext.creatorId = this.auth.currentUser.uid
    return await this.db.ref('infotexts/' + this.customerid).push(infotext)
  }

  updateInfotext = async (infotext) => {
    if (infotext.id) {
      infotext.updaterId = this.auth.currentUser.uid
      return await this.db.ref('infotexts/' + this.customerid + '/' + infotext.id).update(infotext)
    }
  }

  deleteInfotext = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.db.ref('infotexts/' + this.customerid + '/' + id).update(r)
  }

  getInfotext = async (id) => {
    return await this.db.ref('infotexts/' + this.customerid + '/' + id).once('value')
  }


  // Media

  listenMedia = () => {
    return this.firestore.collection('media').doc(this.customerid).collection('library')
  }

  getImages = async () => {
    const mediaDocs = await this.firestore.collection('media').doc(this.customerid).collection('library').get()
    return mediaDocs.docs.map((doc) => { return { ...doc.data(), id: doc.id } }).filter((media) => media.state !== 'deleted').filter((media) => media.mimetype.startsWith('image/'))
  }

  deleteMedia = async (id) => {
    try {
      this.functions.region = 'europe-west1'
      const updateMedia = this.functions.httpsCallable('media-updateMedia')
      const customerid = this.customerid
      const response = await updateMedia({ customerid, remove: true, fileid: id })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }
  
  updateMedia = async (id, name) => {
    try {
      this.functions.region = 'europe-west1'
      const updateMedia = this.functions.httpsCallable('media-updateMedia')
      const customerid = this.customerid
      const response = await updateMedia({ customerid, fileid: id, name })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }


  // Activitytypes

  getActivitytypes = () => {
    return this.getCustomerRef().collection('config').doc('activitytypes')
  }

  // Sports

  getSports = () => {
    return this.getCustomerRef().collection('config').doc('sports')
  }
  

  // latest

  listenLatestReservations = () => {
    return this.firestore.collection('latest').doc(this.customerid).collection('reservations')
  }

  /* Banners */
  createBanner = async (banner) => {
    banner.creatorId = this.auth.currentUser.uid
    return await this.db.ref('infobanners/' + this.customerid).push(banner)
  }

  updateBanner = async (banner) => {
    if (banner.id) {
      banner.updaterId = this.auth.currentUser.uid
      return await this.db.ref('infobanners/' + this.customerid + '/' + banner.id).update(banner)
    }
  }

  deleteBanner = async (id) => {
    let r = { state: 'deleted', updatedId: this.auth.currentUser.uid }
    return await this.db.ref('infobanners/' + this.customerid + '/' + id).update(r)
  }

  getBanner = async (id) => {
    return await this.db.ref('infobanners/' + this.customerid + '/' + id).once('value')
  }

  listenBanners = () => {
    return this.db.ref('infobanners/' + this.customerid)
  }

  /* TextBanner */
  createTextBanner = async (banner) => {
    banner.creatorId = this.auth.currentUser.uid
    return await this.db.ref('textbanners/' + this.customerid).push(banner)
  }
  
  updateTextBanner = async (banner) => {
    if (banner.id) {
      banner.updaterId = this.auth.currentUser.uid
      return await this.db.ref('textbanners/' + this.customerid + '/' + banner.id).update(banner)
    }
  }
  
  deleteTextBanner = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.db.ref('textbanners/' + this.customerid + '/' + id).update(r)
  }
  
  getTextBanner = async (id) => {
    return await this.db.ref('textbanners/' + this.customerid + '/' + id).once('value')
  }
  
  listenTextBanners = () => {
    return this.db.ref('textbanners/' + this.customerid)
  }
  
  /* Recurring listing */

  getRecurringsCsv = async (startdate, enddate) => {
    try {
      this.functions.region = 'europe-west1'
      const updateRole = this.functions.httpsCallable('reports-getRecurringsCsv')
      const response = await updateRole({ startdate, enddate, cid: this.customerid })
      return response
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  forceLights = () => {
    try {
      this.functions.region = 'europe-west1'
      return this.functions.httpsCallable('lights3-forceCommand')({ customerid: this.customerid })
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  listenRecurringSales = () => {
    return this.firestore.collection('customers').doc(this.customerid).collection('recurringsales')
  }

  createRecurringSale = async (document) => {
    document.creatorId = this.auth.currentUser.uid
    document.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('recurringsales').add(document)
  }

  updateRecurringSale = async (document) => {
    if (document.id) {
      document.updaterId = this.auth.currentUser.uid
      document.updated = app.firestore.FieldValue.serverTimestamp()
      return await this.getCustomerRef().collection('recurringsales').doc(document.id).set(document, { merge: true })
    }
  }

  /* Recurring sale inquiries */

  // Enquiries collected by a campaign whose payment method is Tiedustelu. The documents
  // are written by the backend, so this app only ever reads them and moves their status
  // along - it does not create or shape them.
  getRecurringInquiries = async () => {
    const r = await this.getCustomerRef().collection('recurringinquiries').get()
    if (r.empty) return []
    // Filtered client side, NOT with .where('state','!=','deleted'): a != query drops
    // every document that has no state field at all.
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
      .filter((i) => i.state !== 'deleted')
  }

  // Moves an inquiry along and records who did it and when.
  //
  // The log entry goes in with arrayUnion so two admins working the list cannot clobber
  // each other's history, which a read-modify-write of the array would. Note the entry
  // carries a CLIENT timestamp: Firestore rejects a serverTimestamp() sentinel nested
  // inside arrayUnion, so only the top-level fields get the server clock.
  updateRecurringInquiryStatus = async (id, patch) => {
    if (!id || !patch) return { error: 'missing-arguments' }
    const { logentry, ...fields } = patch
    const data = {
      ...fields,
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    if (logentry) data.statuslog = app.firestore.FieldValue.arrayUnion(logentry)
    try {
      await this.getCustomerRef().collection('recurringinquiries').doc(id).set(data, { merge: true })
      return {}
    } catch (error) {
      console.error('Updating inquiry status failed', error)
      return { error: error.message }
    }
  }

  deleteRecurringInquiry = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('recurringinquiries').doc(id).set(data, { merge: true })
  }

  /* Tiedustelut: customers/{cid}/inquiries, every inquiry type (docs/features/inquiries.md) */

  // Written by the customer integrations API. This app only reads them and moves their status
  // along; it never writes the partner's fields. Live, because new inquiries arrive from
  // outside and the partner can change a status too. Deleted ones are dropped client side,
  // for the reason getRecurringInquiries gives, and sorting is the caller's (no index).
  listenInquiries = (onChange, onError) =>
    this.getCustomerRef()
      .collection('inquiries')
      .onSnapshot(
        (snap) => onChange(snap.docs.map((doc) => ({ ...doc.data(), id: doc.id })).filter((i) => i.state !== 'deleted')),
        onError
      )

  // The menu badge: how many are waiting. One equality filter, so no index is needed.
  listenNewInquiryCount = (onChange) =>
    this.getCustomerRef()
      .collection('inquiries')
      .where('status', '==', 'new')
      .onSnapshot(
        (snap) => onChange(snap.docs.filter((doc) => doc.data().state !== 'deleted').length),
        () => onChange(0)
      )

  // Same fields and log as updateRecurringInquiryStatus; the backend writes them identically.
  updateInquiryStatus = async (id, patch) => {
    if (!id || !patch) return { error: 'missing-arguments' }
    const { logentry, ...fields } = patch
    const data = {
      ...fields,
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    if (logentry) data.statuslog = app.firestore.FieldValue.arrayUnion(logentry)
    try {
      await this.getCustomerRef().collection('inquiries').doc(id).set(data, { merge: true })
      return {}
    } catch (error) {
      console.error('Updating inquiry status failed', error)
      return { error: error.message }
    }
  }

  // Soft delete only: the backend hides state 'deleted' from the partner's API as well, and
  // the partner may still hold the id.
  deleteInquiry = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('inquiries').doc(id).set(data, { merge: true })
  }

  deleteRecurringSale = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() }
    return await this.getCustomerRef().collection('recurringsales').doc(id).set(r, { merge: true })
  }

  createRecurringSaleResourcePricing = async (config) => {
    config.type = 'recurringsalepricing'
    normalisePackageBandMode(config, false)
    return await this.getCustomerRef().collection('resourcepricings').add(config)
  }

  addRecurringSaleResourcePricingSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'recurringsalepricing'
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  getRecurringSaleResourcePricings = async () => {
    return await this.getCustomerRef().collection('resourcepricings').where('type', '==', 'recurringsalepricing').get()
  }

  getRecurringSaleResourcePricing = async (id) => {
    return await this.getCustomerRef().collection('resourcepricings').doc(id).get()
  }

  deleteRecurringSaleResourcePricings = async (id) => {
    let r = { state: 'deleted', creatorId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resourcepricings').doc(id).set(r, { merge: true })
  }

  updateRecurringSaleResourcePricing = async (config) => {
    config.type = 'recurringsalepricing'
    normalisePackageBandMode(config, true)
    return await this.getCustomerRef().collection('resourcepricings').doc(config.id).set(config, { merge: true })
  }



  /* Teams - match formats. docs/features/teams.md §4.2
   *
   * A format is the menu a manager books from, so every row needs a stable id: a booking names
   * a ROW, never a duration, and the row id is what the tie stores and what the Phase E guard
   * checks. rjsf's array editor cannot mint ids, so they are filled in on the way to Firestore.
   * Ids are never regenerated for existing rows - doing so would orphan every rubber already
   * booked against them. */
  _withRubberIds = (config) => {
    if (Array.isArray(config.rubbers)) {
      config.rubbers = config.rubbers.map((r) => (r && r.id ? r : { ...r, id: this.getRandomId() }))
    }
    return config
  }

  createTeamFormat = async (format) => {
    const config = this._withRubberIds({ ...format })
    config.state = config.state || 'active'
    config.creatorId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('teamformats').add(config)
  }

  updateTeamFormat = async (format) => {
    const config = this._withRubberIds({ ...format })
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('teamformats').doc(config.id).set(config, { merge: true })
  }

  getTeamFormats = async () => {
    return await this.getCustomerRef().collection('teamformats').get()
  }

  getTeamFormat = async (id) => {
    return await this.getCustomerRef().collection('teamformats').doc(id).get()
  }

  // Soft delete: seasons reference formats by id and ties record which format they were booked
  // under, so a hard delete would leave both pointing at nothing.
  deleteTeamFormat = async (id) => {
    const r = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('teamformats').doc(id).set(r, { merge: true })
  }

  /* Teams - season / availability calendar. docs/features/teams.md §4.1
   *
   * A season is a resourcepricings document under type 'teamspricing', the same
   * discriminated-collection trick as membershippricing / giftcodepricing /
   * recurringsalepricing. Slots are a MAP keyed by slot id, not an array, so two admins
   * editing different slots of the same season merge instead of clobbering. */

  // A cleared number input arrives as undefined, which Firestore rejects and which would
  // leave the old value in place under merge. 0 means "no limit", so store that instead.
  normalizeTeamSeasonBookingDaysAhead = (config) => {
    const n = Number(config.bookingdaysahead)
    config.bookingdaysahead = Number.isInteger(n) && n > 0 ? n : 0
  }

  createTeamSeason = async (config) => {
    this.normalizeTeamSeasonBookingDaysAhead(config)
    config.type = 'teamspricing'
    config.creatorId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('resourcepricings').add(config)
  }

  updateTeamSeason = async (config) => {
    this.normalizeTeamSeasonBookingDaysAhead(config)
    config.type = 'teamspricing'
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('resourcepricings').doc(config.id).set(config, { merge: true })
  }

  // Writes one slot into the season's slots map. Mirrors addRecurringSaleResourcePricingSlot:
  // the slot carries its own configid (the season document it belongs to), set by the
  // calendar when the admin drags out a new slot.
  addTeamSeasonSlot = async (slotConfig) => {
    if (!slotConfig.id) slotConfig.id = this.getRandomId()
    let config = { slots: {} }
    config.slots[slotConfig.id] = slotConfig
    config.type = 'teamspricing'
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(slotConfig.configid)
      .set(config, { merge: true })
  }

  // Removes one slot from the map. A merge write cannot delete a key, so this needs the
  // FieldValue.delete() sentinel against the dotted path.
  deleteTeamSeasonSlot = async (seasonid, slotid) => {
    return await this.getCustomerRef()
      .collection('resourcepricings')
      .doc(seasonid)
      .update({
        ['slots.' + slotid]: app.firestore.FieldValue.delete(),
        updaterId: this.auth.currentUser.uid,
        updated: app.firestore.FieldValue.serverTimestamp(),
      })
  }

  getTeamSeasons = async () => {
    return await this.getCustomerRef().collection('resourcepricings').where('type', '==', 'teamspricing').get()
  }

  getTeamSeason = async (id) => {
    return await this.getCustomerRef().collection('resourcepricings').doc(id).get()
  }

  deleteTeamSeason = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('resourcepricings').doc(id).set(r, { merge: true })
  }

  /* Teams - the teams themselves and their managers. docs/features/teams.md §4.3 */

  // `managerids` is a flat mirror of `managers`, and it is derived on EVERY write rather than
  // being passed in. Firestore cannot array-contains on a field inside an array of objects, so
  // the flat list is the only thing the enduser app's "which teams do I manage" query and the
  // security rule can read - and if the two ever disagree, a manager either loses a team they
  // hold or gains one they do not. Deriving it here is what makes disagreement impossible.
  _withManagerIds = (config) => {
    config.managerids = managerIdsOf(config.managers)
    return config
  }

  createTeam = async (team) => {
    const config = this._withManagerIds({ ...team })
    config.state = config.state || 'active'
    config.creatorId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('teams').add(config)
  }

  updateTeam = async (team) => {
    const config = this._withManagerIds({ ...team })
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    // NOT a merge write, unlike its siblings: dropping the last manager off a team has to
    // actually shorten `managers` and `managerids`, and a merge write leaves an array field it
    // is not given untouched while replacing one it is given wholesale. Merging here would let
    // a removed manager keep their grant forever, so the caller sends the whole document.
    return await this.getCustomerRef().collection('teams').doc(config.id).set(config)
  }

  getTeams = async () => {
    return await this.getCustomerRef().collection('teams').get()
  }

  getTeam = async (id) => {
    return await this.getCustomerRef().collection('teams').doc(id).get()
  }

  // Soft delete: ties record the team they were booked for and reservations carry `teamid`, so
  // a hard delete would leave both pointing at nothing. Same reasoning as deleteTeamFormat.
  deleteTeam = async (id) => {
    const r = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('teams').doc(id).set(r, { merge: true })
  }

  // Grants a manager the right to reach the team booking UI at all, reusing the organisation
  // contact path (§4.3). This says only "this user manages some team"; WHICH teams is answered
  // by querying `managerids`, so the flag deliberately carries no team id.
  //
  // It is never cleared here. One manager may hold several teams, so removing them from one
  // team is not evidence they should lose the grant, and working that out means reading every
  // team - a query the backend does anyway. Revocation is Phase E's, and until it exists a
  // removed manager keeps a flag that opens a UI listing no teams.
  setTeamManagerAcl = async (userid) => {
    return await this.setAcl({ userid, acl: { teammanager: true } })
  }

  /* Teams - ties. docs/features/teams.md §4.4 and Phase D
   *
   * Read and oversight only in this repo. Creating a tie and booking its rubbers are Phase E
   * callables, because the quota claim has to be transactional and the offer-set check has to be
   * authoritative - neither is safe in a client. */

  getTeamTies = async () => {
    return await this.getCustomerRef().collection('teamties').get()
  }

  getTeamTiesForSeason = async (seasonid) => {
    return await this.getCustomerRef().collection('teamties').where('seasonid', '==', seasonid).get()
  }

  getTeamTie = async (id) => {
    return await this.getCustomerRef().collection('teamties').doc(id).get()
  }

  // The admin override: venues get told by phone, and then the venue is the only party who can
  // record it. Written straight to Firestore rather than through `teams-accept`, because that
  // function resolves an opponent's TOKEN (§6) and there is no token in a phone call.
  //
  // `confirmedby` records that this was staff, not the opponent, so the audit trail does not
  // later read as though the opponent clicked the link.
  setTeamTieConfirmation = async ({ tieid, state, opponentteam }) => {
    const update = {
      confirmation: {
        state,
        opponentteam: opponentteam || '',
        confirmedby: { name: 'Ylläpito', email: this.auth.currentUser.email || '' },
        confirmedat: state === 'pending' ? null : app.firestore.FieldValue.serverTimestamp(),
      },
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('teamties').doc(tieid).set(update, { merge: true })
  }

  // Cancelling a tie has to cancel every remaining rubber and release the quota in one go, so it
  // is a Phase E callable and NOT a client write. A client that cancelled the tie document alone
  // would leave the courts booked with nothing pointing at them.
  cancelTeamTie = async (tieid) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('teams-canceltie')
      const response = await call({ customerid: this.customerid, tieid })
      return response.data
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  // Re-sends the manager their acceptance link list. Phase E owns the mail templates.
  resendTeamTieLinks = async (tieid) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('teams-resendlinks')
      const response = await call({ customerid: this.customerid, tieid })
      return response.data
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  /* membership syncs (csvurltomembership, tennisclub) — contract in docs/features/membership-sync.md */
  membershipSyncCall = async (command, payload = {}) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('membershipsync-call')
      const response = await call({ customerid: this.customerid, command, ...payload })
      return response.data
    } catch (error) {
      console.error(error)
      return { error: true, message: error.message }
    }
  }

  getMembershipSyncs = async () => this.membershipSyncCall('get')

  // sourceId null/undefined: add a new source (update) or test unsaved values (test); config null removes the source
  updateMembershipSync = async (integration, sourceId, config) =>
    this.membershipSyncCall('update', { integration, sourceId: sourceId || null, config })

  testMembershipSync = async (integration, sourceId, config) =>
    this.membershipSyncCall('test', { integration, sourceId: sourceId || null, config })

  /* reskontra */
  setduedate = async (date, ids) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('invoicing-reskontra')
      const response = await call({ customerid: this.customerid, command: 'setduedate', payload: { duedate: date, ids } })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  setbillingdate = async (date, ids) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('invoicing-reskontra')
      const response = await call({ customerid: this.customerid, command: 'setbillingdate', payload: { billingdate: date, ids } })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  addpayment = async (amount, id, type, note, paymentdate) => {
    try {
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('invoicing-reskontra')
      const response = await call({ customerid: this.customerid, command: 'addpayment', payload: { amount, id, note, type, paymentdate } })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  sendinvoices = async (ids, type, justcreate, coveringnote, title, creditmemorandumRows) => {
    try {
      if (justcreate === undefined) justcreate = false
      this.functions.region = 'europe-west1'
      const call = this.functions.httpsCallable('invoicing-reskontra')
      const response = await call({ customerid: this.customerid, command: 'sendinvoices', payload: { ids, type, justcreate, coveringnote, title, creditmemorandumRows } })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }


  /* maintenance */
  createMaintenanceContract = async (contract) => {
    contract.creatorId = this.auth.currentUser.uid
    contract.doctype = 'contract'
    return await this.getCustomerRef().collection('maintenance').add(contract)
  }

  updateMaintenanceContract = async (contract) => {
    contract.updaterId = this.auth.currentUser.uid
    contract.doctype = 'contract'
    return await this.getCustomerRef().collection('maintenance').doc(contract.id).set(contract, { merge: true })
  }

  deleteMaintenanceContract = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('maintenance').doc(id).set(r, { merge: true })
  }

  getMaintenanceContract = async (id) => {
    const r = await this.getCustomerRef().collection('maintenance').doc(id).get()
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return {}
    }
  }

  getMaintenanceContracts = async () => {
    let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'contract').get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }



  createMaintenanceServiceDescription = async (servicedescription) => {
    servicedescription.creatorId = this.auth.currentUser.uid
    servicedescription.doctype = 'servicedescription'
    if (servicedescription.taskgroups) {
      servicedescription.taskgroups = servicedescription.taskgroups.map((tg) => { if (!tg.id) { return { ...tg, id: this.getRandomId() } } else { return tg } })
    }
    return await this.getCustomerRef().collection('maintenance').add(servicedescription)
  }

  updateMaintenanceServiceDescription = async (servicedescription) => {
    servicedescription.updaterId = this.auth.currentUser.uid
    servicedescription.doctype = 'servicedescription'
    if (servicedescription.taskgroups) {
      servicedescription.taskgroups = servicedescription.taskgroups.map((tg) => { if (!tg.id) { return { ...tg, id: this.getRandomId() } } else { return tg } })
    }
    return await this.getCustomerRef().collection('maintenance').doc(servicedescription.id).set(servicedescription, { merge: true })
  }

  deleteMaintenanceServiceDescription = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('maintenance').doc(id).set(r, { merge: true })
  }

  getMaintenanceServiceDescription = async (id) => {
    const r = await this.getCustomerRef().collection('maintenance').doc(id).get()
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return {}
    }
  }

  getMaintenanceServiceDescriptions = async (contractid) => {
    let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'servicedescription').where('maintenancecontractid', '==', contractid).get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  createMaintenanceTask = async (task) => {
    task.creatorId = this.auth.currentUser.uid
    task.doctype = 'task'
    return await this.getCustomerRef().collection('maintenance').add(task)
  }

  updateMaintenanceTask = async (task) => {
    task.updaterId = this.auth.currentUser.uid
    task.doctype = 'task'
    return await this.getCustomerRef().collection('maintenance').doc(task.id).set(task, { merge: true })
  }

  deleteMaintenanceTask = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('maintenance').doc(id).set(r, { merge: true })
  }

  getMaintenanceTask = async (id) => {
    const r = await this.getCustomerRef().collection('maintenance').doc(id).get()
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return {}
    }
  }

  getMaintenanceTasks = async (servicedescriptionid) => {
    let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'task').where('servicedescriptionid', '==', servicedescriptionid).get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }


  // getMaintenanceTicketTargets = async () => {
  //   let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'tickettarget').where('state', '!=', 'deleted').get()
  //   if (r.empty) return []
  //   return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  // }

  getMaintenanceTicketTargets = () => {
    return this.getCustomerRef().collection('config').doc('maintenancetargets')
  }

  updateMaintenanceTicketTarget = (config) => {
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return this.getCustomerRef().collection('config').doc('maintenancetargets').set(config, { merge: true })
  }

  getMaintenanceTicketTargetsOnce = async () => {
    let r = await this.getCustomerRef().collection('config').doc('maintenancetargets').get()
    if (r.exists) {
      return r.data().targets
    } else {
      return []
    }
  }

  updateMaintenanceTicketClasses = (config) => {
    config.updaterId = this.auth.currentUser.uid
    config.updated = app.firestore.FieldValue.serverTimestamp()
    return this.getCustomerRef().collection('config').doc('maintenancetargets').set(config, { merge: true })
  }

  getMaintenanceTicketClasses = () => {
    return this.getCustomerRef().collection('config').doc('maintenancetargets')
  }

  getMaintenanceTicketClassesOnce = async () => {
    let r = await this.getCustomerRef().collection('config').doc('maintenancetargets').get()
    if (r.exists) {
      return r.data().classes
    } else {
      return []
    }
  }


  getMaintenanceTickets = async () => {
    let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'ticket')/*.where('state', '!=', 'deleted')*/.get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  createMaintenanceTicket = async (ticket) => {
    ticket.creatorId = this.auth.currentUser.uid
    ticket.doctype = 'ticket'
    delete ticket.createdDate
    delete ticket.updatedDate
    return await this.getCustomerRef().collection('maintenance').add(ticket)
  }

  updateMaintenanceTicket = async (ticket) => {
    ticket.updaterId = this.auth.currentUser.uid
    ticket.doctype = 'ticket'
    ticket.updated = app.firestore.FieldValue.serverTimestamp()
    delete ticket.created
    delete ticket.updatedDate
    delete ticket.createdDate
    delete ticket.createdLocal

    return await this.getCustomerRef().collection('maintenance').doc(ticket.id).set(ticket, { merge: true })
  }

  deleteMaintenanceTicket = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('maintenance').doc(id).set(r, { merge: true })
  }

  getLinksExtension = async (tag) => {
    const links = await app.firestore().collection('links').where('customerid', '==', this.getCustomerId()).get()
    if (links.empty) return []
    const tagLink = links.docs.find((doc) => doc.data().tag === tag)
    if (tagLink) {
      return { ext: tagLink.id }
    } else {
      return { error: true, message: 'No link found' }
    }
  }


  getMaintenancePlanTasks = async () => {
    let r = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'plantask').where('state', '!=', 'deleted').get()
    if (r.empty) return []
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  _getNextDueDate = (fromDate, frequency) => {
    const nextDate = new Date(fromDate)
    switch (frequency) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7)
        break
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1)
        break
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3)
        break
      case 'semiannually':
        nextDate.setMonth(nextDate.getMonth() + 6)
        break
      case 'annually':
        nextDate.setFullYear(nextDate.getFullYear() + 1)
        break 
      case 'biennially':
        nextDate.setFullYear(nextDate.getFullYear() + 2)
        break
      default:
        break
    }
    return nextDate
  }

  createMaintenancePlanTask = async (task) => {
    task.creatorId = this.auth.currentUser.uid
    task.doctype = 'plantask'
    delete task.createdDate
    delete task.updatedDate
    delete task.status
    task.state = 'created'
    // startdate YYYY-MM-DD => new Date(task.startdate)
    task.startdate = task.startdate ? new Date(task.startdate) : null
    if (!task.nextduedate) {
      // count next due date based on last completed date or startdate + frequency (frequency is weekly, monthly, quarterly, semiannually, yearly, biennially)
      const now = new Date()
      let nextDate = null
      if (task.lastcompletiondate) {
        nextDate = new Date(task.lastcompletiondate)
      } else if (task.startdate) {
        nextDate = new Date(task.startdate)
      }
      if (nextDate) {
        nextDate = this._getNextDueDate(nextDate, task.frequency)
        task.nextduedate = nextDate
      } else {
        task.nextduedate = now
      }
    }

    return await this.getCustomerRef().collection('maintenance').add(task)
  } 

  updateMaintenancePlanTask = async (task) => {
    task.updaterId = this.auth.currentUser.uid
    task.doctype = 'plantask'
    task.updated = app.firestore.FieldValue.serverTimestamp()
    delete task.created
    delete task.updatedDate
    delete task.createdDate
    delete task.createdLocal
    return await this.getCustomerRef().collection('maintenance').doc(task.id).set(task, { merge: true })
  }

  deleteMaintenancePlanTask = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    // find related task executions and mark them as deleted
    const executions = await this.getCustomerRef().collection('maintenance').where('doctype', '==', 'taskexecution').where('plantaskid', '==', id).get()
    executions.forEach((doc) => {
      this.getCustomerRef().collection('maintenance').doc(doc.id).set({ state: 'deleted', updaterId: this.auth.currentUser.uid }, { merge: true })
    })
    return await this.getCustomerRef().collection('maintenance').doc(id).set(r, { merge: true })
  }

  getMaintenancePlanTaskExecutions = async (plantaskid) => {
    const ref = this.getCustomerRef().collection('maintenance')
    let r = null
    if (!plantaskid) {
      r = await ref.where('doctype', '==', 'taskexecution').where('state', '!=', 'deleted').get()
    } else {
      r = await ref.where('doctype', '==', 'taskexecution').where('plantaskid', '==', plantaskid).where('state', '!=', 'deleted').get()
    }
    if (r.empty) return []
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
      .sort((a, b) => b.date.toDate() - a.date.toDate())
  }


  createMaintenancePlanTaskExecution = async (execution) => {
    execution.creatorId = this.auth.currentUser.uid
    execution.doctype = 'taskexecution'
    execution.state = 'created'
    execution.date = new Date(execution.date)
    // update plantask's lastcompleteddate and nextduedate
    const plantaskRef = this.getCustomerRef().collection('maintenance').doc(execution.plantaskid)
    const plantaskDoc = await plantaskRef.get()
    if (plantaskDoc.exists) {
      const plantask = plantaskDoc.data()
      console.log('plantask', plantask)
      let nextDate = new Date(execution.date)
      nextDate = this._getNextDueDate(nextDate, plantask.frequency)
      console.log('nextDate', nextDate)
      return Promise.all([this.getCustomerRef().collection('maintenance').add(execution), plantaskRef.set({
        lastcompletiondate: execution.date,
        nextduedate: nextDate
      }, { merge: true })])
    } else {
      return { error: true, message: 'Plantask not found' }
    }
  }

  /* Event Tickets */



  getEventTickets = async () => {
    const r = await this.getCustomerRef().collection('eventtickets').get()
    if (!r.empty) {
      return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
    } else {
      console.error('No event tickets found')
      return []
    }
  }

  // Settle each variation's combinations[] onto a real sibling variation id. Run after variation
  // ids have been assigned so the ids are guaranteed to exist.
  //
  // The picker writes combinationId directly now (combinationVariationWidget), so the common case
  // is a row that is already correct and passes through untouched. Two things still need mapping:
  // a combination variation created in the SAME editing session has no id yet, so the picker falls
  // back to its name as the value; and every row stored before this change holds only the name in
  // `combination`.
  //
  // THE ID WINS OVER THE NAME, which is the opposite of what this did before. Resolving the name
  // first meant that re-pointing a row from one combination to another was silently reverted on
  // save - the stale `combination` name mapped straight back to the old id. It also wrote a bare
  // '' whenever the admin never picked anything, which is exactly the shape that emptied
  // orderLines in paymentv3 and left a paying customer without a ticket on 2026-09-11. An empty
  // reference is still written as '', but utils/eventticketvalidation.js now reports it on the
  // Tapahtumaliput screen instead of letting it reach a payment.
  //
  // `combination` is kept in sync with the resolved variation's name so the stored document stays
  // readable and a later rename cannot resurrect a stale mapping.
  resolveCombinationIds = (variations) => {
    if (!variations || variations.length === 0) return variations
    const byId = {}
    const byName = {}
    variations.forEach((v) => {
      if (v.id) byId[v.id] = v
      if (v.name) byName[v.name] = v
    })
    const resolve = (c) => {
      if (c.combinationId && byId[c.combinationId]) return byId[c.combinationId]
      // The picker's fallback value for a sibling that has no id yet, and the legacy shape.
      if (c.combinationId && byName[c.combinationId]) return byName[c.combinationId]
      if (c.combination && byName[c.combination]) return byName[c.combination]
      return null
    }
    return variations.map((v) => {
      if (!v.combinations || v.combinations.length === 0) return v
      return {
        ...v,
        combinations: v.combinations.map((c) => {
          const target = resolve(c)
          if (!target) return { ...c, combinationId: c.combinationId || '' }
          return { ...c, combinationId: target.id || '', combination: target.name || c.combination || '' }
        }),
      }
    })
  }

  // Assign ids to extrainfo questions (id auto-generated) and remap each
  // variation's selected extrainfo (which may hold titles pre-save) to ids.
  resolveEventTicketExtraInfo = (eventticket) => {
    const extrainfo = eventticket.extrainfo
    if (!extrainfo || extrainfo.length === 0) return eventticket
    const titleToId = {}
    const ids = new Set()
    eventticket.extrainfo = extrainfo.map((e) => {
      const id = e.id || this.getRandomId()
      if (e.title) titleToId[e.title] = id
      ids.add(id)
      return { ...e, id }
    })
    if (eventticket.variations && eventticket.variations.length > 0) {
      eventticket.variations = eventticket.variations.map((v) => {
        if (!v.extrainfo || v.extrainfo.length === 0) return v
        return {
          ...v,
          extrainfo: v.extrainfo
            .map((key) => (ids.has(key) ? key : titleToId[key]))
            .filter(Boolean),
        }
      })
    }
    return eventticket
  }

  createEventTicket = async (eventticket) => {
    eventticket.creatorId = this.auth.currentUser.uid
    eventticket.created = app.firestore.FieldValue.serverTimestamp()
    if (eventticket.variations
      && eventticket.variations.length > 0
      && !eventticket.variations.every((v) => v.id)) {
      eventticket.variations = eventticket.variations.map((v) => {
        if (!v.id) {
          return { ...v, id: this.getRandomId() }
        } else {
          return v
        }
      })
    }
    if (eventticket.variations && eventticket.variations.length === 0) {
      eventticket.variations = [{ id: this.getRandomId(), name: 'Default', price: 0, description: '' }]
      eventticket.availableinshop = 'false' // if no variations, then not available in shop; string to match the schema
    }
    eventticket.variations = this.resolveCombinationIds(eventticket.variations)
    eventticket = this.resolveEventTicketExtraInfo(eventticket)
    return await this.getCustomerRef().collection('eventtickets').add(eventticket)
  }

  updateEventTicket = async (eventticket) => {
    if (eventticket.id) {
      eventticket.updaterId = this.auth.currentUser.uid
      eventticket.updated = app.firestore.FieldValue.serverTimestamp()
      console.log('updating eventticket', eventticket)
      if (eventticket.variations
        && eventticket.variations.length > 0
        && !eventticket.variations.every((v) => v.id)) {
        eventticket.variations = eventticket.variations.map((v) => {
          if (!v.id) {
            return { ...v, id: this.getRandomId() }
          } else {
            return v
          }
        })
      }
      if (eventticket.variations && eventticket.variations.length === 0) {
        eventticket.variations = [{ id: this.getRandomId(), name: 'Default', price: 0, description: '' }]
        eventticket.availableinshop = 'false' // if no variations, then not available in shop; string to match the schema
      }
      eventticket.variations = this.resolveCombinationIds(eventticket.variations)
      eventticket = this.resolveEventTicketExtraInfo(eventticket)
      return await this.getCustomerRef().collection('eventtickets').doc(eventticket.id).set(eventticket, { merge: true })
    }
  }

  // addEventTicket = async (eventticket, payer) => {
  //   eventticket.creatorId = this.auth.currentUser.uid
  //   eventticket.created = this.getToday()
  //   eventticket.user = { ...payer }
  //   eventticket.collection = 'eventtickets'
  //   eventticket.productid = eventticket.id
  //   delete eventticket.updaterId
  //   delete eventticket.id
  //   return await this.getCustomerRef().collection('purchases').add(eventticket)
  // }

  // removePersonEventTicket = async (orderid) => {
  //   return await this.getCustomerRef().collection('purchases').doc(orderid).update({ state: 'deleted', updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() })
  // }

  // addpayment: also record a payment for the purchases, so the tickets count as paid.
  // Note this resolves with { error } instead of throwing - callers must check for it.
  addEventTickets = async (eventtickets, user, addpayment) => {
    // Without an id the backend writes purchases with an empty user, owned by nobody.
    if (!user || !user.id) {
      return { error: 'Henkilöä ei ole vielä tallennettu, lippuja ei voi lisätä.' }
    }
    try {
      this.functions.region = 'europe-west1'
      const addPurchases = this.functions.httpsCallable('eventtickets-addpurchases')
      const customerid = this.customerid
      const data = { customerid, eventtickets, user, addpayment: addpayment !== false }
      const response = await addPurchases(data)
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  // Soft cancel of a whole order: the backend stamps state: 'cancelled' on every purchase
  // sharing the paymenttoken and leaves payments[] and pdfs[] intact.
  //
  // An order, not a line, because the pdf ticket is generated per paymenttoken and lists
  // every line of the order - cancelling one line would leave the customer holding a pdf
  // that still shows it. It also sidesteps the fact that a line cannot be addressed
  // reliably: purchases have no id of their own (purchase.id is the eventticket id) and
  // real orders carry the same variationId twice.
  //
  // Cannot be done client side - users-persondetails returns only this person's purchases,
  // so writing purchases[] back from the person view would erase every other buyer's, and
  // set({merge:true}) replaces arrays wholesale anyway. See docs/functions-to-update.txt.
  // Note this resolves with { error } instead of throwing - callers must check for it.
  cancelEventTicketOrder = async (eventticketid, paymenttoken, userid) => {
    try {
      this.functions.region = 'europe-west1'
      const cancelOrder = this.functions.httpsCallable('eventtickets-cancelpurchase')
      const customerid = this.customerid
      const response = await cancelOrder({ customerid, eventticketid, paymenttoken, userid })
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  /* Event ticket groups - customers/{cid}/eventticketgroups, { name } plus audit fields.
     An event ticket optionally references one by id in its eventticketgroup field. */

  // Filtered client side, NOT with .where('state','!=','deleted'): a != query drops every
  // document that has no state field at all, which is every group written before the first
  // delete. Same reason the equivalent clause is commented out at getMessageListMessages.
  getEventTicketGroups = async () => {
    const r = await this.getCustomerRef().collection('eventticketgroups').get()
    if (r.empty) return []
    return r.docs
      .map((doc) => ({ ...doc.data(), id: doc.id }))
      .filter((g) => g.state !== 'deleted')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }

  createEventTicketGroup = async (name) => {
    const data = {
      name,
      creatorId: this.auth.currentUser.uid,
      created: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('eventticketgroups').add(data)
  }

  updateEventTicketGroup = async (id, name) => {
    if (!id) return
    const data = {
      name,
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('eventticketgroups').doc(id).set(data, { merge: true })
  }

  // Soft delete, like every other delete in this file. Event tickets keep their reference;
  // the dropdown labels a dangling id as poistettu rather than losing the value.
  deleteEventTicketGroup = async (id) => {
    if (!id) return
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('eventticketgroups').doc(id).set(data, { merge: true })
  }

  /* Affiliates */

  // Two collections: affiliates are the partners (the billable party, optionally linked to
  // an organisation), affiliatecampaigns are the codes they run with their own state, dates
  // and commission rules. Both soft delete and both filter deleted rows client side, since
  // a != query drops documents that have no state field at all.

  getAffiliates = async () => {
    const r = await this.getCustomerRef().collection('affiliates').get()
    if (r.empty) return []
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
      .filter((a) => a.state !== 'deleted')
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'fi'))
  }

  getAffiliate = async (id) => {
    const r = await this.getCustomerRef().collection('affiliates').doc(id).get()
    if (!r.exists) return null
    return { ...r.data(), id: r.id }
  }

  createAffiliate = async (affiliate) => {
    affiliate.creatorId = this.auth.currentUser.uid
    affiliate.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('affiliates').add(affiliate)
  }

  updateAffiliate = async (affiliate) => {
    const payload = { ...affiliate }
    payload.updaterId = this.auth.currentUser.uid
    payload.updated = app.firestore.FieldValue.serverTimestamp()
    delete payload.id
    return await this.getCustomerRef().collection('affiliates').doc(affiliate.id).set(payload, { merge: true })
  }

  deleteAffiliate = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('affiliates').doc(id).set(data, { merge: true })
  }

  getAffiliateCampaigns = async () => {
    const r = await this.getCustomerRef().collection('affiliatecampaigns').get()
    if (r.empty) return []
    return r.docs
      .map((doc) => { return { ...doc.data(), id: doc.id } })
      .filter((c) => c.state !== 'deleted')
      .sort((a, b) => String(b.start || '').localeCompare(String(a.start || '')))
  }

  getAffiliateCampaign = async (id) => {
    const r = await this.getCustomerRef().collection('affiliatecampaigns').doc(id).get()
    if (!r.exists) return null
    return { ...r.data(), id: r.id }
  }

  createAffiliateCampaign = async (campaign) => {
    campaign.creatorId = this.auth.currentUser.uid
    campaign.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('affiliatecampaigns').add(campaign)
  }

  // set({merge:true}) replaces arrays wholesale rather than merging them, which is what we
  // want for codes[] and the commission rules: the editor always sends the whole set.
  updateAffiliateCampaign = async (campaign) => {
    const payload = { ...campaign }
    payload.updaterId = this.auth.currentUser.uid
    payload.updated = app.firestore.FieldValue.serverTimestamp()
    delete payload.id
    return await this.getCustomerRef().collection('affiliatecampaigns').doc(campaign.id).set(payload, { merge: true })
  }

  deleteAffiliateCampaign = async (id) => {
    const data = {
      state: 'deleted',
      updaterId: this.auth.currentUser.uid,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }
    return await this.getCustomerRef().collection('affiliatecampaigns').doc(id).set(data, { merge: true })
  }

  // The attributed reservations for a campaign. One equality query per code, run in
  // parallel and merged on id.
  //
  // Single field equality means Firestore's automatic index covers this - no composite
  // index, and no firestore.indexes.json in this repo to add one to. Deliberately NOT
  // where('affiliate','in',codes): the pinned firebase SDK is 8.6.8, which still enforces
  // the old 10 value cap on `in`. A campaign has a handful of codes, so a few small
  // queries is the simpler trade.
  //
  // Date filtering is left to the caller. getReservations() fetches the whole collection
  // and must not be used for this; there is no ranged reservation query anywhere in this
  // file, because ranged reservation data comes from the per day booking index or from a
  // server callable. Scoped to one campaign's codes the result set is small.
  getReservationsByAffiliate = async (codes) => {
    const list = (codes || []).filter(Boolean)
    if (list.length === 0) return []
    const snapshots = await Promise.all(
      list.map((code) =>
        this.getCustomerRef().collection('reservations').where('affiliate', '==', code).get()
      )
    )
    const byId = {}
    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        byId[doc.id] = { ...doc.data(), id: doc.id }
      })
    })
    return Object.keys(byId)
      .map((id) => byId[id])
      .filter((r) => r.state !== 'deleted')
  }

  getMessagingList = async (id) => {
    const r = await this.getCustomerRef().collection('messaginglists').doc(id).get()
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return null
    }
  }

  getMessagingLists = async () => {
    const lists = await this.getCustomerRef().collection('messaginglists').get()
    if (lists.empty) return []
    return lists.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  createMessagingList = async (list) => {
    list.creatorId = this.auth.currentUser.uid
    list.created = app.firestore.FieldValue.serverTimestamp()
    // clear unnessary fields
    if (list.sourcetype === 'reservations') {
      delete list.activitystatus
      delete list.benefitcardstatus
      delete list.membershipstatus
    } else if (list.sourcetype === 'activities') {
      delete list.reservationstatus
      delete list.benefitcardstatus
      delete list.membershipstatus
    } else if (list.sourcetype === 'benefitcards') {
      delete list.reservationstatus
      delete list.activitystatus
      delete list.membershipstatus
    } else if (list.sourcetype === 'memberships') {
      delete list.reservationstatus
      delete list.activitystatus
      delete list.benefitcardstatus
    }
    return await this.getCustomerRef().collection('messaginglists').add(list)
  }

  updateMessagingList = async (list) => {
    if (list.id) {
      list.updaterId = this.auth.currentUser.uid
      list.updated = app.firestore.FieldValue.serverTimestamp()
      console.log('list before clean', JSON.stringify(list, null, 2))
      if (list.sourcetype === 'reservations') {
        delete list.activitystatus
        delete list.benefitcardstatus
        delete list.membershipstatus
      } else if (list.sourcetype === 'activities') {
        delete list.reservationstatus
        delete list.benefitcardstatus
        delete list.membershipstatus
      } else if (list.sourcetype === 'benefitcards') {
        delete list.reservationstatus
        delete list.activitystatus
        delete list.membershipstatus
      } else if (list.sourcetype === 'memberships') {
        delete list.reservationstatus
        delete list.activitystatus
        delete list.benefitcardstatus
      }
      list.lastprocessed = app.firestore.FieldValue.delete()
      delete list.created
      delete list.creatorId
      list.result = app.firestore.FieldValue.delete()
      console.log('Updating messaging list', list)
      return await this.getCustomerRef().collection('messaginglists').doc(list.id).set(list, { merge: true })
    }
  }

  deleteMessagingList = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('messaginglists').doc(id).set(r, { merge: true })
  }

  fetchMessagingListMembers = async (id) => {
    try {
      this.functions.region = 'europe-west1'
      const fetchList = this.functions.httpsCallable('aimarketer-listprocess', { timeout: 500000 })
      const customerid = this.customerid
      const data = { customerid, id }
      const response = await fetchList(data)
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  createMessageListMessage = async (message) => {
    message.creatorId = this.auth.currentUser.uid
    message.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('messagelistmessages').add(message)
  }

  updateMessageListMessage = async (message) => {
    if (message.id) {
      delete message.created
      delete message.creatorId
      message.updaterId = this.auth.currentUser.uid
      message.updated = app.firestore.FieldValue.serverTimestamp()
      return await this.getCustomerRef().collection('messagelistmessages').doc(message.id).set(message, { merge: true })
    }
  }

  deleteMessageListMessage = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('messagelistmessages').doc(id).set(r, { merge: true })
  }

  getMessageListMessages = async (includeDeleted) => {
    let r = await this.getCustomerRef().collection('messagelistmessages')
    // if (!includeDeleted) r = r.where('state', '!=', 'deleted')
    r = await r.get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  getMessageListMessage = async (id) => {
    const r = await this.getCustomerRef().collection('messagelistmessages').doc(id).get() 
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return null
    }
  }

  getMessageListMessageStats = async (id, campaignid) => {
    // get directly from brevo
    try {
      this.functions.region = 'europe-west1'
      const getStats = this.functions.httpsCallable('aimarketer-getmessagelistmessagestats')
      const customerid = this.customerid
      const data = { customerid, messageid: id, campaignid }
      const response = await getStats(data)
      return response.data
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }

  sendMessageListMessage = async (id) => {
    try {
      this.functions.region = 'europe-west1'
      const sendMessage = this.functions.httpsCallable('aimarketer-sendmessagelistmessage')
      const customerid = this.customerid
      const data = { customerid, messageid: id }
      const response = await sendMessage(data)
      return response
    } catch (error) {
      console.error(error)
      return { error: error.message }
    }
  }


  getMessageListMessageTemplates = async () => {
    const [systemtemplates, customertemplates] = await Promise.all([
      this.firestore.collection('messagelistmessagetemplates').get(),
      this.getCustomerRef().collection('messagelistmessagetemplates').get()
    ])
    let templates = []
    // Soft deleted ones are filtered here rather than with a where('state','!=','deleted'):
    // that query would also drop every document without the field, i.e. all the system
    // templates and every template created before soft delete existed.
    if (!systemtemplates.empty) {
      templates = templates.concat(systemtemplates.docs
        .map((doc) => { return { ...doc.data(), id: doc.id, isSystem: true } })
        .filter((t) => t.state !== 'deleted'))
    }
    if (!customertemplates.empty) {
      templates = templates.concat(customertemplates.docs
        .map((doc) => { return { ...doc.data(), id: doc.id, isSystem: false } })
        .filter((t) => t.state !== 'deleted'))
    }
    return templates
  }

  // Customer templates only - the root messagelistmessagetemplates collection holds the
  // system ones and stays read only. isSystem is injected when reading and must never be
  // persisted.
  createMessageListMessageTemplate = async (template) => {
    const data = { ...template }
    delete data.id
    delete data.isSystem
    data.creatorId = this.auth.currentUser.uid
    data.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('messagelistmessagetemplates').add(data)
  }

  updateMessageListMessageTemplate = async (template) => {
    if (!template.id) return
    const data = { ...template }
    delete data.id
    delete data.isSystem
    delete data.created
    delete data.creatorId
    data.updaterId = this.auth.currentUser.uid
    data.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('messagelistmessagetemplates').doc(template.id).set(data, { merge: true })
  }

  deleteMessageListMessageTemplate = async (id) => {
    let r = { state: 'deleted', updaterId: this.auth.currentUser.uid, updated: app.firestore.FieldValue.serverTimestamp() }
    return await this.getCustomerRef().collection('messagelistmessagetemplates').doc(id).set(r, { merge: true })
  }


  /* Surveys */

  getSurveys = async () => {
    const r = await this.getCustomerRef().collection('surveys')
      .where('state', '!=', 'deleted')
      .get()
    if (r.empty) return []
    return r.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
  }

  getSurvey = async (id) => {
    const r = await this.getCustomerRef().collection('surveys').doc(id).get()
    if (r.exists) return { ...r.data(), id: r.id }
    return null
  }

  createSurvey = async (survey) => {
    survey.creatorId = this.auth.currentUser.uid
    survey.created = app.firestore.FieldValue.serverTimestamp()
    survey.state = survey.state || 'draft'
    survey.responseCount = 0
    return await this.getCustomerRef().collection('surveys').add(survey)
  }

  updateSurvey = async (survey) => {
    survey.updaterId = this.auth.currentUser.uid
    survey.updated = app.firestore.FieldValue.serverTimestamp()
    const id = survey.id
    delete survey.id
    await this.getCustomerRef().collection('surveys').doc(id).set(survey, { merge: true })
    return { ...survey, id }
  }

  deleteSurvey = async (id) => {
    return await this.getCustomerRef().collection('surveys').doc(id).set({
      state: 'deleted',
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  activateSurvey = async (id) => {
    return await this.getCustomerRef().collection('surveys').doc(id).set({
      state: 'active',
      activatedAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  closeSurvey = async (id) => {
    return await this.getCustomerRef().collection('surveys').doc(id).set({
      state: 'closed',
      closedAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  scheduleSurvey = async (id, startDate) => {
    return await this.getCustomerRef().collection('surveys').doc(id).set({
      state: 'scheduled',
      scheduledStartDate: startDate,
      scheduledAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  /* Survey Responses */

  getSurveyResponses = async (surveyId) => {
    const r = await this.getCustomerRef()
      .collection('surveys').doc(surveyId)
      .collection('responses')
      .orderBy('submittedAt', 'desc')
      .get()
    if (r.empty) return []
    return r.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
  }

  submitSurveyResponse = async (surveyId, answers, respondent = null) => {
    const responseDoc = {
      surveyId,
      answers,
      submittedAt: app.firestore.FieldValue.serverTimestamp(),
      source: 'app'
    }
    if (respondent) {
      responseDoc.respondentId = respondent.id || null
      responseDoc.respondentEmail = respondent.email || null
      responseDoc.respondentName = respondent.name || null
    }
    const ref = await this.getCustomerRef()
      .collection('surveys').doc(surveyId)
      .collection('responses').add(responseDoc)

    await this.getCustomerRef().collection('surveys').doc(surveyId).set({
      responseCount: app.firestore.FieldValue.increment(1),
      lastResponseAt: app.firestore.FieldValue.serverTimestamp()
    }, { merge: true })
    return ref
  }

  deleteSurveyResponse = async (surveyId, responseId) => {
    await this.getCustomerRef()
      .collection('surveys').doc(surveyId)
      .collection('responses').doc(responseId).delete()
    await this.getCustomerRef().collection('surveys').doc(surveyId).set({
      responseCount: app.firestore.FieldValue.increment(-1)
    }, { merge: true })
  }


  /* News Articles */

  getNewsArticles = async () => {
    const r = await this.getCustomerRef().collection('news')
      .where('state', '!=', 'deleted')
      .get()
    if (r.empty) return []
    return r.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
  }

  getNewsArticle = async (id) => {
    const r = await this.getCustomerRef().collection('news').doc(id).get()
    if (r.exists) return { ...r.data(), id: r.id }
    return null
  }

  createNewsArticle = async (article) => {
    article.creatorId = this.auth.currentUser.uid
    article.created = app.firestore.FieldValue.serverTimestamp()
    article.state = article.state || 'draft'
    return await this.getCustomerRef().collection('news').add(article)
  }

  updateNewsArticle = async (article) => {
    article.updaterId = this.auth.currentUser.uid
    article.updated = app.firestore.FieldValue.serverTimestamp()
    const id = article.id
    delete article.id
    await this.getCustomerRef().collection('news').doc(id).set(article, { merge: true })
    return { ...article, id }
  }

  deleteNewsArticle = async (id) => {
    return await this.getCustomerRef().collection('news').doc(id).set({
      state: 'deleted',
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  publishNewsArticle = async (id) => {
    return await this.getCustomerRef().collection('news').doc(id).set({
      state: 'published',
      publishedAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  scheduleNewsArticle = async (id, publishDate) => {
    return await this.getCustomerRef().collection('news').doc(id).set({
      state: 'scheduled',
      publishDate: publishDate,
      scheduledAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }

  unpublishNewsArticle = async (id) => {
    return await this.getCustomerRef().collection('news').doc(id).set({
      state: 'draft',
      unpublishedAt: app.firestore.FieldValue.serverTimestamp(),
      updaterId: this.auth.currentUser.uid
    }, { merge: true })
  }


  /* Coaching Seasons and Classes */


  getCoachingSeasons = async () => { 
    const seasons = await this.getCustomerRef().collection('coaching').doc('active').collection('seasons').get()
    if (seasons.empty) return []
    return seasons.docs.map((doc) => { return { ...doc.data(), id: doc.id } }).map((season) => {
      if (season.start && season.start.toDate) {
        season.start = moment(season.start.toDate()).format('YYYY-MM-DD')
      }
      if (season.end && season.end.toDate) {
        season.end = moment(season.end.toDate()).format('YYYY-MM-DD')
      }
      if (season.entrystart && season.entrystart.toDate) {
        season.entrystart = moment(season.entrystart.toDate()).format('YYYY-MM-DD')
      }
      if (season.entryend && season.entryend.toDate) {
        season.entryend = moment(season.entryend.toDate()).format('YYYY-MM-DD')
      }
      return season
    })
  }

  createCoachingSeason = async (season) => {
    season.creatorId = this.auth.currentUser.uid
    season.created = app.firestore.FieldValue.serverTimestamp()
    season.start = app.firestore.Timestamp.fromDate(moment(season.start).toDate())
    season.end = app.firestore.Timestamp.fromDate(moment(season.end).toDate())
    season.entrystart = app.firestore.Timestamp.fromDate(moment(season.entrystart).toDate())
    season.entryend = app.firestore.Timestamp.fromDate(moment(season.entryend).toDate())
    return await this.getCustomerRef().collection('coaching').doc('active').collection('seasons').add(season)
  }

  updateCoachingSeason = async (season) => {
    season.updaterId = this.auth.currentUser.uid
    season.updated = app.firestore.FieldValue.serverTimestamp()
    season.start = app.firestore.Timestamp.fromDate(moment(season.start).toDate())
    season.end = app.firestore.Timestamp.fromDate(moment(season.end).toDate())
    season.entrystart = app.firestore.Timestamp.fromDate(moment(season.entrystart).toDate())
    season.entryend = app.firestore.Timestamp.fromDate(moment(season.entryend).toDate())
    return await this.getCustomerRef().collection('coaching').doc('active').collection('seasons').doc(season.id).set(season, { merge: true })
  }

  deleteCoachingSeason = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('coaching').doc('active').collection('seasons').doc(id).set(r, { merge: true })
  }

  getCoachingClasses = async () => {
    const classes = await this.getCustomerRef().collection('coaching').doc('active').collection('classes').get()
    if (classes.empty) return []
    return classes.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  createCoachingClass = async (coachingclass) => {
    coachingclass.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('coaching').doc('active').collection('classes').add(coachingclass)
  }

  updateCoachingClass = async (coachingclass) => {
    coachingclass.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('coaching').doc('active').collection('classes').doc(coachingclass.id).set(coachingclass, { merge: true })
  }

  deleteCoachingClass = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('coaching').doc('active').collection('classes').doc(id).set(r, { merge: true })
  }
  
  getCoachingLevels = async () => {
    const levels = await this.getCustomerRef().collection('coaching').doc('levels').get()
    if (levels.exists) {
      return levels.data().levels ? levels.data().levels.filter((l) => l.state !== 'deleted') : []
    } else {
      return []
    }
  }

  createCoachingLevel = async (level) => {
    let levels = await this.getCoachingLevels()
    level.id = this.getRandomId()
    levels.push(level)
    return await this.getCustomerRef().collection('coaching').doc('levels').set({ levels })
  }

  updateCoachingLevel = async (level) => {
    let levels = await this.getCoachingLevels()
    levels = levels.map((l) => { if (l.id === level.id) { return level } else { return l } })
    return await this.getCustomerRef().collection('coaching').doc('levels').set({ levels })
  }

  deleteCoachingLevel = async (id) => {
    let levels = await this.getCoachingLevels()
    levels = levels.filter((l) => l.id !== id)
    return await this.getCustomerRef().collection('coaching').doc('levels').set({ levels })
  }

  getCoachingBreaks = async (internal) => {
    const breaks = await this.getCustomerRef().collection('coaching').doc('breaks').get()
    if (breaks.exists) {
      if(internal) {
        return breaks.data().dates ? breaks.data().dates : []
      } else {
        return breaks.data().dates ? breaks.data().dates.map((d) => {
          d.start = moment(d.start.toDate()).format('YYYY-MM-DD')
          d.end = moment(d.end.toDate()).format('YYYY-MM-DD')
          return d
        }) : []
      }
    }
    else {
      return []
    }
  }

  createCoachingBreak = async (breakdate) => {
    let breaks = await this.getCoachingBreaks(true)
    breakdate.id = this.getRandomId()
    breakdate.start = app.firestore.Timestamp.fromDate(moment(breakdate.start).toDate())
    breakdate.end = app.firestore.Timestamp.fromDate(moment(breakdate.end).toDate())
    breaks.push(breakdate)
    return await this.getCustomerRef().collection('coaching').doc('breaks').set({ dates: breaks })
  }

  updateCoachingBreak = async (breakdate) => {
    let breaks = await this.getCoachingBreaks(true)
    breakdate.start = app.firestore.Timestamp.fromDate(moment(breakdate.start).toDate())
    breakdate.end = app.firestore.Timestamp.fromDate(moment(breakdate.end).toDate())
    breaks = breaks.map((b) => { if (b.id === breakdate.id) { return breakdate } else { return b } })
    return await this.getCustomerRef().collection('coaching').doc('breaks').set({ dates: breaks })
  }

  deleteCoachingBreak = async (id) => {
    let breaks = await this.getCoachingBreaks(true)
    breaks = breaks.filter((b) => b.id !== id)
    return await this.getCustomerRef().collection('coaching').doc('breaks').set({ dates: breaks })
  }

  getCoachingPricings = async () => {
    const pricings = await this.getCustomerRef().collection('coaching').doc('active').collection('pricings').get()
    if (pricings.empty) return []
    return pricings.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  createCoachingPricing = async (pricing) => {
    pricing.creatorId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('coaching').doc('active').collection('pricings').add(pricing)
  }

  updateCoachingPricing = async (pricing) => {
    pricing.updaterId = this.auth.currentUser.uid
    return await this.getCustomerRef().collection('coaching').doc('active').collection('pricings').doc(pricing.id).set(pricing, { merge: true })
  }

  deleteCoachingPricing = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('coaching').doc('active').collection('pricings').doc(id).set(r, { merge: true })
  }

  getCoachingApplications = async () => {
    const applications = await this.getCustomerRef().collection('coaching').doc('active').collection('applications').get()
    if (applications.empty) return []
    return applications.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  getCoachingApplication = async (id) => {
    const r = await this.getCustomerRef().collection('coaching').doc('active').collection('applications').doc(id).get()
    if (r.exists) {
      return { ...r.data(), id: r.id }
    } else {
      return {}
    }
  }

  createCoachingApplication = async (application) => {
    application.creatorId = this.auth.currentUser.uid
    application.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('coaching').doc('active').collection('applications').add(application)
  }

  updateCoachingApplication = async (application) => {
    application.updaterId = this.auth.currentUser.uid
    application.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('coaching').doc('active').collection('applications').doc(application.id).set(application, { merge: true })
  }

  deleteCoachingApplication = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('coaching').doc('active').collection('applications').doc(id).set(r, { merge: true })
  }

  getCoachingEntries = async (season, coachingclass) => {
    if (!season || !coachingclass) return []
    const entries = await this.getCustomerRef().collection('coaching').doc('active').collection('entries').where('seasonid', '==', season).where('classid', '==', coachingclass).get()
    if (entries.empty) return []
    return entries.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  addCoachingEntry = async (entry) => {
    entry.creatorId = this.auth.currentUser.uid
    if (!entry.seasonid) throw new Error('Season ID is required')
    if (!entry.classid) throw new Error('Class ID is required')
    entry.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('coaching').doc('active').collection('entries').add(entry)
  }

  addCoachingEntriesBatch = async (entries, season, coachingclass) => {
    if(!season || !coachingclass) throw new Error('Season ID and Class ID are required')
    const batch = this.firestore.batch()
    entries.forEach((entry) => {
      const docRef = this.getCustomerRef().collection('coaching').doc('active').collection('entries').doc()
      entry.creatorId = this.auth.currentUser.uid
      entry.created = app.firestore.FieldValue.serverTimestamp()
      entry.seasonid = season
      entry.classid = coachingclass
      batch.set(docRef, entry)
    })
    return await batch.commit()
  }

  deleteCoachingEntry = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('coaching').doc('active').collection('entries').doc(id).set(r, { merge: true })
  }

  updateCoachingEntry = async (entry) => {
    entry.updaterId = this.auth.currentUser.uid
    entry.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('coaching').doc('active').collection('entries').doc(entry.id).set(entry, { merge: true })
  }

  // venuelocations
  
  createVenueLocation = async (location) => {
    location.creatorId = this.auth.currentUser.uid
    location.created = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('venuelocations').add(location)
  }
  
  getVenueLocations = async () => {
    const r = await this.getCustomerRef().collection('venuelocations').get()
    if (r.empty) return []
    return r.docs.map((doc) => { return { ...doc.data(), id: doc.id } })
  }

  updateVenueLocation = async (location) => {
    location.updaterId = this.auth.currentUser.uid
    location.updated = app.firestore.FieldValue.serverTimestamp()
    return await this.getCustomerRef().collection('venuelocations').doc(location.id).set(location, { merge: true })
  }

  deleteVenueLocation = async (id) => {
    const r = { state: 'deleted', updaterId: this.auth.currentUser.uid }
    return await this.getCustomerRef().collection('venuelocations').doc(id).set(r, { merge: true })
  }

  // *** Feature Voting System ***
  // All collections are under featurevoting/data as subcollections

  getFeatureVotingRef = () => {
    return this.firestore.collection('featurevoting').doc('data')
  }

  getCurrentVotePeriod = () => {
    return moment().format('YYYY-MM')
  }

  getFeatureVoteBalance = async () => {
    const userId = this.auth.currentUser.uid
    const votePeriod = this.getCurrentVotePeriod()
    const docId = `${userId}_${votePeriod}`

    const doc = await this.getFeatureVotingRef().collection('balances').doc(docId).get()

    if (doc.exists) {
      return doc.data()
    }

    // Create default balance for new month
    const defaultBalance = {
      id: docId,
      userId,
      votePeriod,
      totalVotes: 3,
      usedVotes: 0,
      remainingVotes: 3,
      updated: app.firestore.FieldValue.serverTimestamp(),
    }

    await this.getFeatureVotingRef().collection('balances').doc(docId).set(defaultBalance)
    return defaultBalance
  }

  listenApprovedFeatureRequests = (callback) => {
    return this.getFeatureVotingRef()
      .collection('requests')
      .where('state', 'in', ['approved', 'planned', 'implemented'])
      .orderBy('voteCount', 'desc')
      .onSnapshot((snapshot) => {
        const requests = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        callback(requests)
      })
  }

  getUserFeatureVotes = async () => {
    const userId = this.auth.currentUser.uid
    const votePeriod = this.getCurrentVotePeriod()

    const snapshot = await this.getFeatureVotingRef()
      .collection('votes')
      .where('userId', '==', userId)
      .where('votePeriod', '==', votePeriod)
      .get()

    return snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }))
  }

  submitFeatureRequest = async (request) => {
    const user = this.auth.currentUser

    const newRequest = {
      ...request,
      state: 'pending',
      voteCount: 0,
      submitterId: user.uid,
      submitterEmail: user.email,
      submitterCustomerId: this.customerid,
      aiRefinedText: request.description,
      created: app.firestore.FieldValue.serverTimestamp(),
      updated: app.firestore.FieldValue.serverTimestamp(),
    }

    return await this.getFeatureVotingRef().collection('requests').add(newRequest)
  }

  castFeatureVote = async (requestId, voteCount) => {
    this.functions.region = 'europe-west1'
    const castVote = this.functions.httpsCallable('featurevoting-castVote')
    const response = await castVote({
      requestId,
      voteCount,
      votePeriod: this.getCurrentVotePeriod(),
      customerId: this.customerid,
    })
    return response.data
  }

  removeFeatureVote = async (requestId, voteCount) => {
    this.functions.region = 'europe-west1'
    const removeVote = this.functions.httpsCallable('featurevoting-removeVote')
    const response = await removeVote({
      requestId,
      voteCount,
      votePeriod: this.getCurrentVotePeriod(),
      customerId: this.customerid,
    })
    return response.data
  }

  getGeminiAssistance = async (input, history) => {
    this.functions.region = 'europe-west1'
    const geminiAssist = this.functions.httpsCallable('featurevoting-geminiAssist')
    const response = await geminiAssist({ input, history })
    return response.data
  }

  // Venuemanager assistant (falcon functions/assistant.js). The club comes from the caller's token on the
  // server, so none of these send a customer id.
  newAssistantConversationId = () => this.firestore.collection('random').doc().id

  assistantSend = async (text, conversationId, newConversationId) => {
    this.functions.region = 'europe-west1'
    const send = this.functions.httpsCallable('assistant-send', { timeout: 540000 })
    const response = await send(conversationId ? { text, conversationId } : { text, newConversationId })
    return response.data
  }

  assistantList = async () => {
    this.functions.region = 'europe-west1'
    const list = this.functions.httpsCallable('assistant-list')
    const response = await list({})
    return response.data
  }

  assistantDelete = async (conversationId) => {
    this.functions.region = 'europe-west1'
    const del = this.functions.httpsCallable('assistant-delete')
    const response = await del({ conversationId })
    return response.data
  }

  assistantResolve = async (conversationId, proposalId, decision) => {
    this.functions.region = 'europe-west1'
    const resolve = this.functions.httpsCallable('assistant-resolve', { timeout: 540000 })
    const response = await resolve({ conversationId, proposalId, decision })
    return response.data
  }

  listenAssistantProposals = (conversationId) => {
    return this.firestore.collection('customers').doc(this.customerid).collection('assistant').doc(conversationId).collection('proposals')
  }

  listenAssistantMessages = (conversationId) => {
    return this.firestore.collection('customers').doc(this.customerid).collection('assistant').doc(conversationId).collection('messages').orderBy('seq')
  }

}

export default Firebase
